/**
 * DockAppearanceEditor — Visual sandbox for the DynamicDock widget.
 *
 * Covers only the settings that matter for DynamicDock:
 *   Anchor · Dock Style (glass) · Chatbot Zone · Channel Apps · Chat Window
 *
 * Zones 1 (Channels Entry) and 3 (Channels Menu) from the old editor are gone —
 * the pill IS the entry and the menu.
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  BlockStack, InlineStack, Text, Button, Select, RangeSlider, TextField, Badge,
  Toast, Box,
  Tooltip
} from '@shopify/polaris';
import {
  Bot, MessageCircle, X, Mail, Phone, MapPin, Lock, Info,
  Globe, Send, ChevronLeft, ChevronRight, Minus, GripVertical, Search, Monitor, Smartphone,
  MessageSquare, Sparkles, ArrowLeft, LifeBuoy, SquarePen
} from 'lucide-react';
import {
  WhatsAppIcon, MessengerIcon, InstagramIcon, XIcon, TelegramIcon,
  TikTokIcon, SMSIcon, LineIcon, ViberIcon, SnapchatIcon, WeChatIcon, LinkedInIcon,
} from '@reach/ui/icons';
import { CHANNEL_METADATA, calculateStoreStatus, type ScheduleConfig } from '@reach/core';
import { Reorder, motion, AnimatePresence } from 'framer-motion';
import { merchantFetch } from '../api';
import { ReachLogo } from '../../Logo';


// ─── Types ───────────────────────────────────────────────────────────────────

type GlassStyle = 'glass' | 'tinted' | 'solid';
type DockState = 'closed' | 'expanded' | 'allApps' | 'chat';
type ZoneId = 'global' | 'button' | 'dock' | 'chatbot' | 'app_item' | null;
type DeviceMode = 'desktop' | 'mobile';

export interface ChannelAppConfig {
  enabled?: boolean;
  label?: string;
  value?: string;
  color?: string;
  showOpen?: boolean;
  showClosed?: boolean;
  showOnMobile?: boolean;
  showOnDesktop?: boolean;
  order?: number;
}

export interface DockAppearanceConfig {
  platform?: string;
  isEnabled: boolean;
  anchorColor: string;
  botColor?: string;
  widgetPosition: 'left' | 'right';
  desktopButtonSize?: number;
  mobileButtonSize?: number;
  buttonRadius: number;
  mobileRadius: number;
  buttonIcon: 'square' | 'round';
  desktopSideMargin: number;
  mobileSideMargin: number;
  desktopBottomMargin: number;
  mobileBottomMargin: number;
  buttonZIndex?: number;
  offlineMode: 'follow_apps' | 'chatbot' | 'back_in_x' | 'chatbot_follow_apps' | 'hide';
  globalFontFamily: string;
  dockGlassStyle: GlassStyle;
  dockOpacity: number;
  dockTintColor: string;
  dockMaxVisibleApps: number;
  dockTooltipsEnabled?: boolean;
  backInLabel?: string;
  backInBgColor?: string;
  backInTextColor?: string;
  backInLabelColor?: string;
  backInFontSize?: number;
  backInFontFamily?: string;
  backInFontWeight?: number;
  anchorAvatar: string;
  launcherAnimation?: 'none' | 'pulse' | 'float' | 'spin' | 'rotate' | 'rotate_gradient' | 'breath' | 'glow';
  launcherHoverEffect?: 'rotate' | 'expand' | 'lift' | 'tilt' | 'none';
  showStatusDot?: boolean;
  statusDotTooltipEnabled?: boolean;
  statusDotOnlineLabel?: string;
  statusDotLunchLabel?: string;
  statusDotClosedLabel?: string;
  chatbotEntryAvatar: string;
  chatbotTipEnabled: boolean;
  chatbotTipText: string;
  chatbotTipBgColor: string;
  chatbotTipTextColor: string;
  chatbotTipFontSize: number;
  chatbotTipFontWeight: number;
  chatbotTipFontFamily: string;
  botName: string;
  chatbotHeaderTagline: string;
  chatbotAvatar: string;
  chatbotHeaderAvatarEnabled?: boolean;
  chatbotHeaderStatusDotEnabled?: boolean;
  chatbotHeaderTextColor: string;
  chatbotHeaderFontFamily: string;
  chatbotHeaderFontSize: number;
  chatbotHeaderStyle?: 'solid' | 'gradient';
  chatbotHeaderFontWeight: number;
  chatbotBodyFontFamily: string;
  chatbotBodyFontSize: number;
  chatbotBodyFontWeight: number;
  chatbotSuggestionFontFamily?: string;
  chatbotSuggestionFontSize?: number;
  chatbotSuggestionFontWeight?: number;
  chatbotContainerColor: string;
  chatbotContainerOpacity: number;
  welcomeMsg: string;
  initialSuggestions: string;
  apps: Record<string, ChannelAppConfig>;
  // Schedule
  timezone?: string;
  startHour?: number;
  endHour?: number;
  weekendBehavior?: 'none' | 'sunday' | 'all';
  lunchEnabled?: boolean;
  lunchStart?: number;
  lunchEnd?: number;
  holidayEnabled?: boolean;
  holidays?: (string | HolidayEntry)[];

  planTier: string;
  mobileDrawerBgColor?: string;
  mobileDrawerOpacity?: number;
  // Container launch style
  launchStyle?: 'dock' | 'container' | 'chat_first';
  containerGlassStyle?: 'glass' | 'tinted' | 'solid';
  containerTintColor?: string;
  containerOpacity?: number;
  dockContainerLabels?: boolean;
  dockContainerTooltips?: boolean;
  containerColumns?: number;
  dockContainerTitleEnabled?: boolean;
  dockContainerTitle?: string;
  dockContainerTitleAlign?: 'left' | 'center' | 'right';
  mobileContainerLabels?: boolean;
  mobileContainerTooltips?: boolean;
  mobileContainerTitleEnabled?: boolean;
  mobileContainerTitle?: string;
  mobileContainerTitleAlign?: 'left' | 'center' | 'right';
}

export interface HolidayEntry {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
}

interface DockAppearanceEditorProps {
  config: DockAppearanceConfig;
  onChange: (key: string, value: unknown) => void;
  onSave: () => void;
  shopDomain: string;
  isGrowthPlus: boolean;
  isAgentPlus: boolean;
  isDirty: boolean;
  setLocalHolidays?: (val: (string | HolidayEntry)[]) => void;
  setShowHolidayModal?: (val: boolean) => void;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const CHANNEL_ICON_MAP: Record<string, React.ElementType> = {
  whatsapp: WhatsAppIcon,
  messenger: MessengerIcon,
  instagram: InstagramIcon,
  telegram: TelegramIcon,
  twitter: XIcon,
  sms: SMSIcon,
  email: Mail,
  phone: Phone,
  google_maps: MapPin,
  tiktok: TikTokIcon,
  line: LineIcon,
  viber: ViberIcon,
  snapchat: SnapchatIcon,
  wechat: WeChatIcon,
  linkedin: LinkedInIcon,
};

const CHANNEL_META = Object.entries(CHANNEL_METADATA).reduce((acc, [key, meta]) => {
  acc[key] = {
    ...meta,
    icon: CHANNEL_ICON_MAP[key] || MessageCircle,
  };
  return acc;
}, {} as Record<string, { label: string; color: string; fieldLabel: string; placeholder: string; hint: string; icon: React.ElementType }>);

const CHANNEL_KEYS = Object.keys(CHANNEL_META);
const DEFAULT_WIDGET_Z_INDEX = 399;
const DEFAULT_WIDGET_Z_INDEX_CAP = 999;
const PLATFORM_WIDGET_Z_INDEX_CAP: Record<string, number> = {
  shopify: 950,
  wordpress: 950,
};

function getWidgetZIndexCap(platform?: string): number {
  if (!platform) return DEFAULT_WIDGET_Z_INDEX_CAP;
  return PLATFORM_WIDGET_Z_INDEX_CAP[platform.toLowerCase()] ?? DEFAULT_WIDGET_Z_INDEX_CAP;
}

function clampWidgetZIndex(value: unknown, platform?: string): number {
  const cap = getWidgetZIndexCap(platform);
  const numeric = Number(value);
  const fallback = Math.min(DEFAULT_WIDGET_Z_INDEX, cap);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(1, Math.min(cap, Math.round(numeric)));
}

function getStatusDotColor(status: 'open' | 'closed' | 'lunch' | 'holiday') {
  if (status === 'open') return '#22c55e';
  if (status === 'lunch' || status === 'holiday') return '#f59e0b';
  return '#ef4444';
}

function getStatusDotLabel(
  status: 'open' | 'closed' | 'lunch' | 'holiday',
  config: Pick<DockAppearanceConfig, 'statusDotOnlineLabel' | 'statusDotLunchLabel' | 'statusDotClosedLabel'>
) {
  if (status === 'open') return config.statusDotOnlineLabel || 'Online';
  if (status === 'lunch') return config.statusDotLunchLabel || 'Out for lunch';
  return config.statusDotClosedLabel || 'Closed';
}

const FONT_FAMILY_OPTIONS = [
  { label: 'Inherit from site', value: 'inherit' },
  // Sans-serif
  { label: 'Plus Jakarta Sans', value: 'Plus Jakarta Sans' },
  { label: 'Inter', value: 'Inter' },
  { label: 'Roboto', value: 'Roboto' },
  { label: 'Open Sans', value: 'Open Sans' },
  { label: 'Lato', value: 'Lato' },
  { label: 'Montserrat', value: 'Montserrat' },
  { label: 'Poppins', value: 'Poppins' },
  { label: 'Nunito', value: 'Nunito' },
  { label: 'Nunito Sans', value: 'Nunito Sans' },
  { label: 'Outfit', value: 'Outfit' },
  { label: 'DM Sans', value: 'DM Sans' },
  { label: 'Raleway', value: 'Raleway' },
  { label: 'Rubik', value: 'Rubik' },
  { label: 'Work Sans', value: 'Work Sans' },
  { label: 'Figtree', value: 'Figtree' },
  { label: 'Manrope', value: 'Manrope' },
  { label: 'Josefin Sans', value: 'Josefin Sans' },
  { label: 'Quicksand', value: 'Quicksand' },
  { label: 'Karla', value: 'Karla' },
  { label: 'Mulish', value: 'Mulish' },
  { label: 'Ubuntu', value: 'Ubuntu' },
  { label: 'Barlow', value: 'Barlow' },
  { label: 'Lexend', value: 'Lexend' },
  { label: 'Calibri', value: 'Calibri' },

  // Serif
  { label: 'Cardo', value: 'Cardo' },
  { label: 'Playfair Display', value: 'Playfair Display' },
  { label: 'Merriweather', value: 'Merriweather' },
  { label: 'Lora', value: 'Lora' },
  { label: 'Cormorant Garamond', value: 'Cormorant Garamond' },
  // Monospace
  { label: 'JetBrains Mono', value: 'JetBrains Mono' },
  { label: 'Fira Code', value: 'Fira Code' },
  // System
  { label: 'Arial', value: 'Arial' },
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Helvetica', value: 'Helvetica' },
  { label: 'Verdana', value: 'Verdana' },
];
const FONT_SIZE_OPTIONS = [
  { label: '10px', value: '10' }, { label: '11px', value: '11' },
  { label: '12px', value: '12' }, { label: '13px', value: '13' },
  { label: '14px', value: '14' }, { label: '15px', value: '15' },
  { label: '16px', value: '16' }, { label: '17px', value: '17' },
  { label: '18px', value: '18' }, { label: '19px', value: '19' },
  { label: '20px', value: '20' }
];
const FONT_WEIGHT_OPTIONS = [
  { label: 'Regular (400)', value: '400' }, { label: 'Medium (500)', value: '500' },
  { label: 'SemiBold (600)', value: '600' }, { label: 'Bold (700)', value: '700' },
  { label: 'Extra Bold (800)', value: '800' }, { label: 'Black (900)', value: '900' }
];
const DESKTOP_PREVIEW_WIDTH = 1600;
const MOBILE_VIEWPORT = 390;
const PHONE_BORDER = 10; // matches `border: '10px solid'` on the phone frame div
const DOCK_PREVIEW_CACHE_KEY = 'reach_dock_preview_cache_v2';
const DOCK_PREVIEW_URL_KEY = 'reach_dock_preview_url_v2';

type PreviewAnchorLayout = {
  isLeft: boolean;
  btnSize: number;
  left: number;
  top: number;
};

type SurfaceStyleTokens = {
  background: string;
  border: string;
  boxShadow: string;
};

function getPreviewAnchorLayout({
  config,
  isMobile,
  canvasWidth,
  canvasHeight,
  dragPos,
  isDragging,
  effectivePosition,
}: {
  config: DockAppearanceConfig;
  isMobile: boolean;
  canvasWidth?: number;
  canvasHeight?: number;
  dragPos?: { x: number; y: number };
  isDragging?: boolean;
  effectivePosition?: 'left' | 'right';
}): PreviewAnchorLayout {
  const btnSize = isMobile
    ? (typeof config.mobileButtonSize === 'number' ? config.mobileButtonSize : 52)
    : (typeof config.desktopButtonSize === 'number' ? config.desktopButtonSize : 60);
  const isLeft = (effectivePosition ?? config.widgetPosition) === 'left';

  if (isDragging && dragPos) {
    let offsetLeft = 0;
    let offsetTop = 0;

    if (isMobile) {
      offsetLeft = ((canvasWidth ?? MOBILE_VIEWPORT) - MOBILE_VIEWPORT) / 2 + PHONE_BORDER;
      const outerPhoneH = Math.min(780, Math.max(0, (canvasHeight ?? 812) - 32));
      offsetTop = ((canvasHeight ?? outerPhoneH) - outerPhoneH) / 2 + PHONE_BORDER;
    }

    return {
      isLeft,
      btnSize,
      left: dragPos.x - offsetLeft,
      top: dragPos.y - offsetTop,
    };
  }

  const viewportWidth = isMobile
    ? MOBILE_VIEWPORT - PHONE_BORDER * 2
    : (canvasWidth ?? DESKTOP_PREVIEW_WIDTH);
  const viewportHeight = isMobile
    ? Math.min(780, Math.max(0, (canvasHeight ?? 812) - 32)) - PHONE_BORDER * 2
    : (canvasHeight ?? 800);
  const sideMargin = isMobile ? (config.mobileSideMargin ?? 16) : (config.desktopSideMargin ?? 24);
  const bottomMargin = isMobile ? (config.mobileBottomMargin ?? 16) : (config.desktopBottomMargin ?? 24);

  return {
    isLeft,
    btnSize,
    left: isLeft ? sideMargin : Math.max(0, viewportWidth - sideMargin - btnSize),
    top: Math.max(0, viewportHeight - bottomMargin - btnSize),
  };
}

function readStoredPreviewUrl(fallbackUrl: string): string {
  try {
    return sessionStorage.getItem(DOCK_PREVIEW_URL_KEY) || fallbackUrl;
  } catch {
    return fallbackUrl;
  }
}

function readStoredPreviewHtml(url: string): string | null {
  if (!url.trim()) return null;

  try {
    const raw = sessionStorage.getItem(DOCK_PREVIEW_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as { url?: string; html?: string };
    return parsed.url === url && typeof parsed.html === 'string' ? sanitizePreviewHtml(parsed.html) : null;
  } catch {
    return null;
  }
}

function sanitizePreviewHtml(html: string): string {
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/\son\w+=(?:"[^"]*"|'[^']*')/gi, '');
}

const TIMEZONES = [
  {
    title: 'North America',
    options: [
      { value: 'America/Anchorage',  label: 'Alaska (UTC-9)' },
      { value: 'America/Los_Angeles', label: 'Pacific Time — US & Canada (UTC-8)' },
      { value: 'America/Vancouver',  label: 'Vancouver (UTC-8)' },
      { value: 'America/Denver',     label: 'Mountain Time — US & Canada (UTC-7)' },
      { value: 'America/Phoenix',    label: 'Arizona — no DST (UTC-7)' },
      { value: 'America/Chicago',    label: 'Central Time — US & Canada (UTC-6)' },
      { value: 'America/Mexico_City', label: 'Mexico City (UTC-6)' },
      { value: 'America/New_York',   label: 'Eastern Time — US & Canada (UTC-5)' },
      { value: 'America/Halifax',    label: 'Atlantic Time — Canada (UTC-4)' },
      { value: 'America/St_Johns',   label: 'Newfoundland (UTC-3:30)' },
    ],
  },
  {
    title: 'South & Central America',
    options: [
      { value: 'America/Bogota',                     label: 'Bogota, Lima, Quito (UTC-5)' },
      { value: 'America/Caracas',                    label: 'Caracas (UTC-4)' },
      { value: 'America/Sao_Paulo',                  label: 'Brasilia (UTC-3)' },
      { value: 'America/Argentina/Buenos_Aires',     label: 'Buenos Aires (UTC-3)' },
      { value: 'America/Noronha',                    label: 'Fernando de Noronha (UTC-2)' },
    ],
  },
  {
    title: 'Atlantic & UTC',
    options: [
      { value: 'Atlantic/Cape_Verde', label: 'Cape Verde (UTC-1)' },
      { value: 'Atlantic/Azores',     label: 'Azores (UTC-1)' },
      { value: 'UTC',                 label: 'UTC / GMT (UTC+0)' },
      { value: 'Europe/London',       label: 'London, Dublin, Lisbon (UTC+0)' },
    ],
  },
  {
    title: 'Europe',
    options: [
      { value: 'Europe/Paris',    label: 'Paris, Berlin, Amsterdam, Rome (UTC+1)' },
      { value: 'Europe/Helsinki', label: 'Helsinki, Athens, Eastern Europe (UTC+2)' },
      { value: 'Europe/Moscow',   label: 'Moscow (UTC+3)' },
    ],
  },
  {
    title: 'Africa & Middle East',
    options: [
      { value: 'Africa/Algiers', label: 'West Central Africa (UTC+1)' },
      { value: 'Africa/Cairo',   label: 'Cairo, Johannesburg (UTC+2)' },
      { value: 'Asia/Beirut',    label: 'Beirut (UTC+2)' },
      { value: 'Africa/Nairobi', label: 'Nairobi, Baghdad, Riyadh (UTC+3)' },
      { value: 'Asia/Tehran',    label: 'Tehran (UTC+3:30)' },
      { value: 'Asia/Dubai',     label: 'Dubai, Abu Dhabi, Muscat (UTC+4)' },
      { value: 'Asia/Kabul',     label: 'Kabul (UTC+4:30)' },
    ],
  },
  {
    title: 'South & Central Asia',
    options: [
      { value: 'Asia/Karachi',   label: 'Islamabad, Karachi (UTC+5)' },
      { value: 'Asia/Tashkent',  label: 'Tashkent (UTC+5)' },
      { value: 'Asia/Kolkata',   label: 'Mumbai, New Delhi, Kolkata (UTC+5:30)' },
      { value: 'Asia/Kathmandu', label: 'Kathmandu (UTC+5:45)' },
      { value: 'Asia/Dhaka',     label: 'Dhaka, Almaty (UTC+6)' },
    ],
  },
  {
    title: 'East & SE Asia',
    options: [
      { value: 'Asia/Yangon',    label: 'Yangon (Myanmar) (UTC+6:30)' },
      { value: 'Asia/Bangkok',   label: 'Bangkok, Hanoi, Jakarta (UTC+7)' },
      { value: 'Asia/Singapore', label: 'Singapore, Beijing, Hong Kong (UTC+8)' },
      { value: 'Asia/Tokyo',     label: 'Tokyo, Seoul, Osaka (UTC+9)' },
    ],
  },
  {
    title: 'Australia',
    options: [
      { value: 'Australia/Perth',      label: 'Perth (UTC+8)' },
      { value: 'Australia/Eucla',      label: 'Eucla (UTC+8:45)' },
      { value: 'Australia/Adelaide',   label: 'Adelaide, Darwin (UTC+9:30)' },
      { value: 'Australia/Sydney',     label: 'Sydney, Melbourne (UTC+10)' },
      { value: 'Australia/Brisbane',   label: 'Brisbane — no DST (UTC+10)' },
      { value: 'Australia/Lord_Howe',  label: 'Lord Howe Island (UTC+10:30)' },
    ],
  },
  {
    title: 'Pacific',
    options: [
      { value: 'Etc/GMT+12',        label: 'International Date Line West (UTC-12)' },
      { value: 'Pacific/Pago_Pago', label: 'American Samoa, Midway (UTC-11)' },
      { value: 'Pacific/Honolulu',  label: 'Hawaii (UTC-10)' },
      { value: 'Pacific/Marquesas', label: 'Marquesas Islands (UTC-9:30)' },
      { value: 'Pacific/Noumea',    label: 'Solomon Is., New Caledonia (UTC+11)' },
      { value: 'Pacific/Auckland',  label: 'Auckland, Wellington (UTC+12)' },
      { value: 'Pacific/Fiji',      label: 'Fiji, Marshall Islands (UTC+12)' },
      { value: 'Pacific/Chatham',   label: 'Chatham Islands (UTC+12:45)' },
      { value: 'Pacific/Apia',      label: 'Samoa, Tonga (UTC+13)' },
      { value: 'Pacific/Kiritimati', label: 'Line Islands (UTC+14)' },
    ],
  },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  label: i === 0 ? '12:00 AM' : i === 12 ? '12:00 PM' : i > 12 ? `${i - 12}:00 PM` : `${i}:00 AM`,
  value: `${i}`,
}));

const DOCK_CSS = `
  .dock-zone {
    cursor: pointer;
    transition: all 0.12s ease;
    -webkit-tap-highlight-color: transparent;
  }
  .dock-zone:focus { outline: none; }
  .dock-zone:hover { box-shadow: 0 0 0 1.5px color-mix(in srgb, var(--theme-action-accent) 42%, #0f172a); }
  .dock-zone.dock-hover { box-shadow: 0 0 0 1.5px color-mix(in srgb, var(--theme-action-accent) 42%, #0f172a); }
  .dock-zone.dock-active {
    outline: 2px solid var(--theme-action-accent);
    outline-offset: 1px;
    z-index: 100 !important;
    position: relative;
    animation: dock-active-pulse 1.2s ease-out infinite;
  }
  .dock-zone.dock-launcher-active {
    outline: 2px solid var(--theme-action-accent);
    outline-offset: 1px;
    z-index: 100 !important;
    position: relative;
  }
  .dock-save-btn {
    transition: transform 0.15s ease, box-shadow 0.2s ease, opacity 0.2s;
  }
  .dock-save-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 28px color-mix(in srgb, var(--theme-action-accent) 28%, transparent);
  }
  .dock-save-btn:active {
    transform: translateY(0);
  }
  @keyframes dock-active-pulse {
    0% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--theme-action-accent) 32%, transparent); }
    100% { box-shadow: 0 0 0 10px transparent; }
  }
  @keyframes dock-blink {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0; }
  }
`;

// ─── Glass Token System (mirrors DynamicDock.tsx) ─────────────────────────

interface GlassTokens {
  pillBg: string; backdropFilter: string; border: string; shadow: string;
  textColor: string; dividerColor: string; chatZoneBg: string; chatZoneBorder: string;
  xBg: string; xBorder: string; xColor: string; xShadow: string;
  xBackdropFilter?: string;
  sheetBg?: string;
  sheetBorder?: string;
}

function hexToRgbParts(hex: string) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) } : { r: 37, g: 99, b: 235 };
}

function getContrastColor(hex: string) {
  const { r, g, b } = hexToRgbParts(hex);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#111827' : '#ffffff';
}

// Brand icon SVGs often should stay white even on "bright" brand colors (e.g. Snapchat yellow).
function getBrandIconColor(hex: string) {
  const { r, g, b } = hexToRgbParts(hex);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.88 ? '#111827' : '#ffffff';
}

// tintColor: resolved color for the 'tinted' preset (dockTintColor ?? anchorColor)
function buildGlassTokens(style: GlassStyle, opacity: number, tintColor: string): GlassTokens {
  const o = opacity / 100;

  if (style === 'solid') {
    const { r, g, b } = hexToRgbParts(tintColor);
    const textColor = getContrastColor(tintColor);
    const isDark = textColor === '#ffffff';

    return {
      pillBg: `rgba(${r},${g},${b},${o})`,
      backdropFilter: 'blur(0px) saturate(100%)', // Consistency for smoother transition
      border: isDark ? `rgba(255,255,255,0.2)` : `rgba(0,0,0,0.1)`,
      shadow: `0 8px 32px rgba(0,0,0,0.12), inset 0 1px 0 ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
      textColor,
      dividerColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
      chatZoneBg: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
      chatZoneBorder: 'none',
      xBg: `rgba(${r},${g},${b},${o})`,
      xBorder: isDark ? `rgba(255,255,255,0.2)` : `rgba(0,0,0,0.1)`,
      xColor: textColor,
      xShadow: `0 4px 12px rgba(0,0,0,0.1)`,
      xBackdropFilter: 'blur(0px) saturate(100%)',
      sheetBg: `rgba(${r},${g},${b},0.98)`,
      sheetBorder: `rgba(${textColor === '#ffffff' ? '255,255,255' : '0,0,0'},0.1)`,
    };
  }

  if (style === 'tinted') {
    const { r, g, b } = hexToRgbParts(tintColor);
    // #7: Hybrid dynamic scaling: 12px min + 10px scaled
    const blurRadius = Math.round(12 + (o * 10)); // 12px -> 22px
    const saturation = Math.round(140 + (o * 60)); // 140% -> 200%

    return {
      pillBg: `rgba(${r},${g},${b},${Math.min(o * 0.75, 0.85)})`,
      backdropFilter: `blur(${blurRadius}px) saturate(${saturation}%)`,
      border: `rgba(${r},${g},${b},0.45)`,
      shadow: `0 8px 32px rgba(${r},${g},${b},0.3), inset 0 1px 0 rgba(255,255,255,0.18)`,
      textColor: '#ffffff', dividerColor: 'rgba(255,255,255,0.18)',
      chatZoneBg: 'rgba(255,255,255,0.15)', chatZoneBorder: 'none',
      xBg: `rgba(${r},${g},${b},${Math.min(o * 0.75, 0.85)})`,
      xBorder: `1px solid rgba(${r},${g},${b},0.45)`,
      xColor: 'rgba(255,255,255,0.8)',
      xShadow: `0 2px 12px rgba(${r},${g},${b},0.3), inset 0 1px 0 rgba(255,255,255,0.2)`,
      xBackdropFilter: `blur(${blurRadius}px) saturate(${saturation}%)`,
      sheetBg: `rgba(${r},${g},${b},${Math.min(o * 0.95, 0.98)})`,
      sheetBorder: `rgba(255,255,255,0.1)`,
    };
  }

  // Default to 'glass'
  const blurRadius = Math.round(4 + (o * 12)); // 4px -> 16px
  const saturation = Math.round(120 + (o * 60)); // 120% -> 180%

  return {
    pillBg: `rgba(255,255,255,${o * 0.7})`,
    backdropFilter: `blur(${blurRadius}px) saturate(${saturation}%)`,
    border: 'rgba(255,255,255,0.4)',
    shadow: '0 8px 32px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.6)',
    textColor: '#111827', dividerColor: 'rgba(0,0,0,0.1)',
    chatZoneBg: 'rgba(255,255,255,0.0)', chatZoneBorder: '1px solid rgba(255,255,255,0.4)',
    xBg: 'rgba(255,255,255,0.2)', xBorder: '1px solid rgba(255,255,255,0.4)', xColor: 'rgba(0,0,0,0.5)',

    xShadow: '0 2px 12px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.5)',
    xBackdropFilter: `blur(${blurRadius}px) saturate(${saturation}%)`,
    sheetBg: `rgba(255,255,255,0.95)`,
    sheetBorder: `rgba(0,0,0,0.05)`,
  };
}

// ─── Shared UI Primitives (same patterns as AppearanceEditor) ─────────────

function ColorPicker({ value, onChange, resetValue }: { value: string; onChange: (v: string) => void; resetValue?: string }) {
  const [local, setLocal] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { setLocal(value); }, [value]);
  useEffect(() => {
    const t = setTimeout(() => { if (local !== value) onChange(local); }, 150);
    return () => clearTimeout(t);
  }, [local]);
  return (
    <InlineStack gap="150" blockAlign="center">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px 5px 6px', borderRadius: 8, border: '1px solid #d1d5db', background: 'white', boxShadow: '0 1px 2px rgba(0,0,0,0.06)', position: 'relative' }}>
        <div onClick={() => inputRef.current?.click()} style={{ width: 22, height: 22, borderRadius: 5, background: local, border: '1px solid rgba(0,0,0,0.14)', flexShrink: 0, cursor: 'pointer' }} />
        <input
          type="text" value={local.toUpperCase()} spellCheck={false}
          onChange={e => { let v = e.target.value; if (!v.startsWith('#')) v = '#' + v; setLocal(v); if (/^#([0-9A-F]{3}){1,2}$/i.test(v)) onChange(v); }}
          style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'monospace', border: 'none', background: 'transparent', width: 64, padding: 0, outline: 'none' }}
        />
        <input ref={inputRef} type="color" value={local} onChange={e => setLocal(e.target.value)} style={{ position: 'absolute', opacity: 0, width: 1, height: 1, top: 0, left: 0, pointerEvents: 'none' }} tabIndex={-1} />
      </div>
      {resetValue && local.toLowerCase() !== resetValue.toLowerCase() && (
        <Button size="micro" variant="tertiary" onClick={() => { setLocal(resetValue); onChange(resetValue); }}>Reset</Button>
      )}
    </InlineStack>
  );
}

function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button onClick={() => !disabled && onChange(!checked)} disabled={disabled}
      style={{
        width: 40, height: 22, borderRadius: 11,
        background: checked ? 'var(--dock-toggle-on)' : 'var(--dock-toggle-off)',
        border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
        opacity: disabled ? 0.6 : 1
      }}>
      <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'var(--white)', position: 'absolute', top: 3, left: checked ? 21 : 3, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </button>
  );
}

function SegmentedControl({ options, value, onChange, disabled }: { options: { label: string; value: string }[]; value: string; onChange: (v: string) => void; disabled?: boolean }) {
  return (
    <InlineStack gap="0">
      {options.map((opt, i) => (
        <button key={opt.value} onClick={() => !disabled && onChange(opt.value)} disabled={disabled}
          style={{ padding: '5px 11px', border: `1px solid ${value === opt.value ? 'var(--dock-segment-on)' : 'var(--dock-segment-off-border)'}`, background: value === opt.value ? 'var(--dock-segment-on)' : (disabled ? 'var(--dock-segment-disabled-bg)' : 'var(--dock-segment-off-bg)'), color: value === opt.value ? 'var(--white)' : (disabled ? 'var(--dock-segment-disabled-text)' : 'var(--dock-segment-off-text)'), cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 500, borderRadius: i === 0 ? '6px 0 0 6px' : i === options.length - 1 ? '0 6px 6px 0' : '0', marginLeft: i > 0 ? -1 : 0, position: 'relative', zIndex: value === opt.value ? 1 : 0 }}>
          {opt.label}
        </button>
      ))}
    </InlineStack>
  );
}

function ChoiceList({
  value,
  onChange,
  options,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { label: string; value: string; description?: string }[];
  disabled?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => !disabled && onChange(opt.value)}
            disabled={disabled}
            style={{
              width: '100%',
              textAlign: 'left',
              padding: '12px 14px',
              borderRadius: 12,
              border: active ? '1px solid var(--brand-secondary)' : '1px solid #dbe3ef',
              background: active ? 'linear-gradient(135deg, rgba(19,42,64,0.08) 0%, rgba(52,144,176,0.16) 100%)' : '#ffffff',
              color: '#0f172a',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
              boxShadow: active ? '0 8px 20px rgba(8, 145, 178, 0.12)' : '0 1px 2px rgba(15, 23, 42, 0.04)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: active ? 'var(--brand-secondary)' : '#0f172a', whiteSpace: 'normal', lineHeight: 1.35 }}>
                {opt.label}
              </div>
              {opt.description && (
                <div style={{ marginTop: 4, fontSize: 12, color: '#64748b', whiteSpace: 'normal', lineHeight: 1.45 }}>
                  {opt.description}
                </div>
              )}
            </div>
            <div
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                flexShrink: 0,
                marginTop: 1,
                border: active ? '5px solid var(--brand-secondary)' : '2px solid #cbd5e1',
                background: '#fff',
              }}
            />
          </button>
        );
      })}
    </div>
  );
}

function FieldRow({ label, children, locked }: { label: React.ReactNode; children: React.ReactNode; locked?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, minHeight: 40, borderBottom: '1px solid var(--dock-field-border)', paddingBottom: 8, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--dock-field-label)' }}>{label}</span>
        {locked && <Badge tone="attention" size="small">Growth+</Badge>}
      </div>
      <div style={{ opacity: locked ? 0.45 : 1, pointerEvents: locked ? 'none' : 'auto', flexShrink: 0 }}>{children}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: 'var(--dock-section-bg)', border: '1px solid var(--dock-section-border)', borderRadius: 18, padding: 20, boxShadow: '0 10px 24px rgba(15,23,42,0.05), inset 0 1px 0 rgba(255,255,255,0.72)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--dock-section-title)', marginBottom: 16, letterSpacing: '-0.01em' }}>{title}</h3>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {children}
      </div>
    </div>
  );
}

function FontBlock({ prefix, family, size, weight, onFamily, onSize, onWeight, locked }: {
  prefix: string; family: string; size: number; weight: number;
  onFamily: (v: string) => void; onSize: (v: number) => void; onWeight: (v: number) => void;
  locked?: boolean;
}) {
  const fontLabel = prefix ? `${prefix} font` : 'Font';
  const sizeLabel = prefix ? `${prefix} size` : 'Size';
  const weightLabel = prefix ? `${prefix} weight` : 'Weight';

  return (
    <>
      <FieldRow label={fontLabel} locked={locked}>
        <div style={{ width: 155 }}><Select label="" labelHidden options={FONT_FAMILY_OPTIONS} value={family || 'inherit'} onChange={onFamily} /></div>
      </FieldRow>
      <FieldRow label={sizeLabel} locked={locked}>
        <div style={{ width: 100 }}><Select label="" labelHidden options={FONT_SIZE_OPTIONS} value={String(size || 13)} onChange={v => onSize(Number(v))} /></div>
      </FieldRow>
      <FieldRow label={weightLabel} locked={locked}>
        <div style={{ width: 155 }}><Select label="" labelHidden options={FONT_WEIGHT_OPTIONS} value={String(weight || 600)} onChange={v => onWeight(Number(v))} /></div>
      </FieldRow>
    </>
  );
}

function TypographyGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        border: '1px solid rgba(148, 163, 184, 0.16)',
        borderRadius: 14,
        padding: '16px 18px',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.72) 0%, rgba(248,250,252,0.92) 100%)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b', marginBottom: 12 }}>
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {children}
      </div>
    </div>
  );
}

// ─── Zone Panels ─────────────────────────────────────────────────────────────

function AnchorPanel({ config, onChange, isGrowthPlus, isAgentPlus, setLocalHolidays, setShowHolidayModal, startDragMode, isDragMode, onFinishDragMode, draftOfflineMode, onDraftOfflineModeChange, onStartOfflineStrategyEvaluation, onCommitOfflineStrategyEvaluation, onCancelOfflineStrategyEvaluation }: {
  config: DockAppearanceConfig; onChange: (k: string, v: unknown) => void; isGrowthPlus: boolean; isAgentPlus: boolean;
  setLocalHolidays?: (h: any[]) => void;
  setShowHolidayModal?: (s: boolean) => void;
  startDragMode: () => void;
  isDragMode: boolean;
  onFinishDragMode: () => void;
  draftOfflineMode: string;
  onDraftOfflineModeChange: (value: string) => void;
  onStartOfflineStrategyEvaluation: () => void;
  onCommitOfflineStrategyEvaluation: () => void;
  onCancelOfflineStrategyEvaluation: () => void;
}) {
  const [showOfflineStrategyOptions, setShowOfflineStrategyOptions] = useState(false);
  const offlineStrategyOptions = [
    {
      label: 'Show configured apps',
      value: 'follow_apps',
      description: 'Keep showing your enabled support channels after hours. Each channel still respects its own “show when closed” setting.',
    },
    {
      label: 'Show “Back in X” message',
      value: 'back_in_x',
      description: 'Replace the launcher with a live return-time message such as “Back in 2 hr 30 min” or “Back in 45 mins”.',
    },
    {
      label: 'Hide widget',
      value: 'hide',
      description: 'Hide the widget completely outside business hours.',
    },
    ...(isAgentPlus
      ? [
        {
          label: 'Show chatbot only',
          value: 'chatbot',
          description: 'Keep the assistant available after hours, without showing the other channel apps.',
        },
        {
          label: 'Show chatbot and configured apps',
          value: 'chatbot_follow_apps',
          description: 'Keep the assistant visible after hours and also show any channels that are allowed when closed.',
        },
      ]
      : []),
  ];
  const effectiveOfflineMode = showOfflineStrategyOptions ? draftOfflineMode : (config.offlineMode || 'follow_apps');
  const currentOfflineStrategy = offlineStrategyOptions.find((opt) => opt.value === effectiveOfflineMode) || offlineStrategyOptions[0];
  const widgetZIndexCap = getWidgetZIndexCap(config.platform);
  const zIndexHelpText = config.platform === 'shopify' || config.platform === 'wordpress'
    ? `This store runs on ${config.platform}. Max widget z-index is capped at ${widgetZIndexCap} so native platform UI stays above Reach.`
    : `Max widget z-index is ${widgetZIndexCap}. Keep this below your theme cart and sticky UI layers.`;

  const openOfflineStrategyEditor = () => {
    onStartOfflineStrategyEvaluation();
    setShowOfflineStrategyOptions(true);
  };

  const closeOfflineStrategyEditor = () => {
    onCancelOfflineStrategyEvaluation();
    setShowOfflineStrategyOptions(false);
  };

  const confirmOfflineStrategyEditor = () => {
    onCommitOfflineStrategyEvaluation();
    setShowOfflineStrategyOptions(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Visibility Toggle - Special prominent style */}
      <div id="widget-visibility-anchor" style={{ background: config.isEnabled ? 'var(--dock-live-bg)' : 'var(--dock-live-off-bg)', border: `1px solid ${config.isEnabled ? 'var(--dock-live-border)' : 'var(--dock-live-off-border)'}`, borderRadius: 16, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'all 0.2s' }}>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: config.isEnabled ? 'var(--dock-live-title)' : 'var(--dock-live-off-title)', marginBottom: 2 }}>Widget is {config.isEnabled ? 'Live' : 'Hidden'}</h3>
          <p style={{ fontSize: 13, color: config.isEnabled ? 'var(--dock-live-text)' : 'var(--dock-live-off-text)' }}>Toggle whether the widget loads on your storefront.</p>
        </div>
        <Toggle checked={config.isEnabled} onChange={v => onChange('isEnabled', v)} />
      </div>

      <Section title="Identity">
        <FieldRow label="Button color" locked={!isGrowthPlus}>
          <ColorPicker value={isGrowthPlus ? config.anchorColor : '#000000'} onChange={v => onChange('anchorColor', v)} />
        </FieldRow>
        <FieldRow label="Button icon" locked={!isGrowthPlus}>
          <SegmentedControl value={config.buttonIcon || 'square'} onChange={v => onChange('buttonIcon', v)}
            options={[{ label: 'Square', value: 'square' }, { label: 'Round', value: 'round' }]} />
        </FieldRow>
        <FieldRow label="Avatar URL" locked={!isGrowthPlus}>
          <div style={{ width: 155 }}><TextField label="" labelHidden value={config.anchorAvatar || ''} onChange={v => onChange('anchorAvatar', v)} autoComplete="off" placeholder="https://…" /></div>
        </FieldRow>
      </Section>

      <Section title="Position">
        <div style={{ marginTop: 8, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 14,
              padding: '12px 14px',
              borderRadius: 14,
              border: '1px solid #e2e8f0',
              background: isDragMode
                ? 'linear-gradient(180deg, rgba(219,234,254,0.9) 0%, rgba(255,255,255,0.98) 100%)'
                : 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.98) 100%)',
              boxShadow: isDragMode
                ? '0 12px 24px -24px rgba(37,99,235,0.35)'
                : '0 8px 18px -24px rgba(15,23,42,0.14)',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Button position</span>
              <span style={{ fontSize: 12, color: '#64748b', lineHeight: 1.45 }}>
                Drag the launcher directly inside the preview.
              </span>
            </div>
            <button
              type="button"
              onClick={isDragMode ? onFinishDragMode : startDragMode}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                borderRadius: 10,
                border: isDragMode
                  ? '1px solid color-mix(in srgb, var(--theme-action-accent) 38%, #cbd5e1)'
                  : '1px solid color-mix(in srgb, var(--theme-action-accent) 24%, #cbd5e1)',
                background: isDragMode
                  ? 'linear-gradient(135deg, color-mix(in srgb, var(--theme-action-accent) 22%, white) 0%, color-mix(in srgb, var(--theme-action-accent) 12%, white) 100%)'
                  : 'color-mix(in srgb, var(--theme-action-accent) 10%, white)',
                color: 'color-mix(in srgb, var(--theme-action-accent) 78%, #0f172a)',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                boxShadow: isDragMode
                  ? '0 10px 20px -18px color-mix(in srgb, var(--theme-action-accent) 48%, transparent)'
                  : '0 8px 18px -20px color-mix(in srgb, var(--theme-action-accent) 30%, transparent)',
                flexShrink: 0,
              }}
            >
              <GripVertical size={14} />
              <span>{isDragMode ? 'Done positioning' : 'Drag in Preview'}</span>
            </button>
          </div>
          <p style={{ margin: 0, fontSize: 11, color: '#94a3b8', lineHeight: 1.45 }}>
            Position is saved per device. Use the editor&apos;s device toggle to switch between Desktop and Mobile.
          </p>
        </div>

        <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16, marginTop: 12, marginBottom: 8 }}>
          <Text as="h3" variant="bodySm" fontWeight="bold" tone="subdued">MANUAL POSITION</Text>
        </div>
        <FieldRow label="Corner">
          <SegmentedControl value={config.widgetPosition} onChange={v => onChange('widgetPosition', v)}
            options={[{ label: 'Left', value: 'left' }, { label: 'Right', value: 'right' }]} />
        </FieldRow>
        <FieldRow label="Desktop Side margin">
          <div style={{ width: 180 }}><RangeSlider label="" labelHidden min={0} max={80} step={1} value={config.desktopSideMargin ?? 40} onChange={v => onChange('desktopSideMargin', v)} output /></div>
        </FieldRow>
        <FieldRow label="Desktop Bottom margin">
          <div style={{ width: 180 }}><RangeSlider label="" labelHidden min={0} max={80} step={1} value={config.desktopBottomMargin ?? 40} onChange={v => onChange('desktopBottomMargin', v)} output /></div>
        </FieldRow>
        <FieldRow label="Mobile side margin">
          <div style={{ width: 180 }}><RangeSlider label="" labelHidden min={0} max={60} step={1} value={config.mobileSideMargin ?? 30} onChange={v => onChange('mobileSideMargin', v)} output /></div>
        </FieldRow>
        <FieldRow label="Mobile bottom margin">
          <div style={{ width: 180 }}><RangeSlider label="" labelHidden min={0} max={60} step={1} value={config.mobileBottomMargin ?? 30} onChange={v => onChange('mobileBottomMargin', v)} output /></div>
        </FieldRow>

        <FieldRow label="Z-index">
          <div style={{ width: '90px' }}>
            <TextField
              label=""
              labelHidden
              type="number"
              value={String(clampWidgetZIndex(config.buttonZIndex, config.platform))}
              onChange={v => onChange('buttonZIndex', clampWidgetZIndex(v, config.platform))}
              max={widgetZIndexCap}
              autoComplete="off"
            />
          </div>
        </FieldRow>
        <Box background="bg-surface-brand-selected" padding="200" borderRadius="200">
          <Text as="p" variant="bodyXs" tone="subdued">
            <strong>Tip:</strong> {zIndexHelpText}
          </Text>
        </Box>
      </Section>

      <Section title="Size">
        <FieldRow label="Desktop (px)" locked={!isGrowthPlus}>
          <div style={{ width: 180 }}><RangeSlider label="" labelHidden min={40} max={80} step={2} value={(config.desktopButtonSize as number) || 60} onChange={v => onChange('desktopButtonSize', v)} output /></div>
        </FieldRow>
        <FieldRow label="Desktop Roundness (Radius in px)" locked={!isGrowthPlus}>
          <div style={{ width: 180 }}><RangeSlider label="" labelHidden min={0} max={40} value={config.buttonRadius ?? 40} onChange={v => onChange('buttonRadius', v)} output /></div>
        </FieldRow>
        <FieldRow label="Mobile (px)" locked={!isGrowthPlus}>
          <div style={{ width: 180 }}><RangeSlider label="" labelHidden min={36} max={68} step={2} value={(config.mobileButtonSize as number) || 52} onChange={v => onChange('mobileButtonSize', v)} output /></div>
        </FieldRow>
        <FieldRow label="Mobile Roundness (Radius in px)" locked={!isGrowthPlus}>
          <div style={{ width: 180 }}><RangeSlider label="" labelHidden min={0} max={34} value={config.mobileRadius ?? 34} onChange={v => onChange('mobileRadius', v)} output /></div>
        </FieldRow>
      </Section>

      <Section title="Animation">
        <FieldRow label="Idle animation" locked={!isGrowthPlus}>
          <div style={{ width: 155 }}><Select label="" labelHidden options={[
            { label: 'None', value: 'none' }, { label: 'Pulse (ring)', value: 'pulse' },
            { label: 'Breath (scale)', value: 'breath' }, { label: 'Float (bob)', value: 'float' },
            { label: 'Glow (light)', value: 'glow' }, { label: 'Spin (360)', value: 'spin' },
            { label: 'Rotate Gradient', value: 'rotate_gradient' },
          ]} value={config.launcherAnimation || 'none'} onChange={v => onChange('launcherAnimation', v)} /></div>
        </FieldRow>
        <FieldRow label="Hover effect" locked={!isGrowthPlus}>
          <div style={{ width: 155 }}><Select label="" labelHidden options={[
            { label: 'None', value: 'none' }, { label: 'Rotate & Scale', value: 'rotate' }, { label: 'Expand', value: 'expand' },
            { label: 'Lift', value: 'lift' }, { label: 'Tilt (3D)', value: 'tilt' },
          ]} value={config.launcherHoverEffect || 'rotate'} onChange={v => onChange('launcherHoverEffect', v)} /></div>
        </FieldRow>
      </Section>

      <Section title="Status Dot">
        <FieldRow label="Show status dot" locked={!isGrowthPlus}>
          <Toggle checked={isGrowthPlus ? (config.showStatusDot ?? true) : false} onChange={v => onChange('showStatusDot', v)} />
        </FieldRow>
        {(config.showStatusDot ?? true) && (
          <>
            <FieldRow label="Show status on hover" locked={!isGrowthPlus}>
              <Toggle
                checked={isGrowthPlus ? (config.statusDotTooltipEnabled ?? true) : false}
                onChange={v => onChange('statusDotTooltipEnabled', v)}
              />
            </FieldRow>
            <FieldRow
              label={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 0 2px rgba(255,255,255,0.9)' }} />Open label</span>}
              locked={!isGrowthPlus}
            >
              <div style={{ width: 155, opacity: (config.statusDotTooltipEnabled ?? true) ? 1 : 0.45 }}>
                <TextField label="" labelHidden value={config.statusDotOnlineLabel || 'Online'} onChange={v => onChange('statusDotOnlineLabel', v)} autoComplete="off" disabled={!(config.statusDotTooltipEnabled ?? true)} />
              </div>
            </FieldRow>
            <FieldRow
              label={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b', boxShadow: '0 0 0 2px rgba(255,255,255,0.9)' }} />Lunch label</span>}
              locked={!isGrowthPlus}
            >
              <div style={{ width: 155, opacity: (config.statusDotTooltipEnabled ?? true) ? 1 : 0.45 }}>
                <TextField label="" labelHidden value={config.statusDotLunchLabel || 'Out for lunch'} onChange={v => onChange('statusDotLunchLabel', v)} autoComplete="off" disabled={!(config.statusDotTooltipEnabled ?? true)} />
              </div>
            </FieldRow>
            <FieldRow
              label={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 0 2px rgba(255,255,255,0.9)' }} />Closed label</span>}
              locked={!isGrowthPlus}
            >
              <div style={{ width: 155, opacity: (config.statusDotTooltipEnabled ?? true) ? 1 : 0.45 }}>
                <TextField label="" labelHidden value={config.statusDotClosedLabel || 'Closed'} onChange={v => onChange('statusDotClosedLabel', v)} autoComplete="off" disabled={!(config.statusDotTooltipEnabled ?? true)} />
              </div>
            </FieldRow>
            <p style={{ fontSize: 11, color: '#64748b', marginTop: -4, marginBottom: 0 }}>
              The hover label is desktop-only. Holiday status reuses the closed label.
            </p>
          </>
        )}
      </Section>

      {isGrowthPlus && (
        <Section title="Tip Bubble">
          <FieldRow label="Show tip">
            <Toggle checked={config.chatbotTipEnabled} onChange={v => onChange('chatbotTipEnabled', v)} />
          </FieldRow>
          {config.chatbotTipEnabled && (
            <>
              <FieldRow label="Tip text">
                <div style={{ width: 155 }}><TextField label="" labelHidden value={config.chatbotTipText || ''} onChange={v => onChange('chatbotTipText', v)} autoComplete="off" /></div>
              </FieldRow>
              <FieldRow label="Background">
                <ColorPicker value={config.chatbotTipBgColor || '#D81B60'} onChange={v => onChange('chatbotTipBgColor', v)} />
              </FieldRow>
              <FieldRow label="Text color">
                <ColorPicker value={config.chatbotTipTextColor || '#ffffff'} onChange={v => onChange('chatbotTipTextColor', v)} />
              </FieldRow>
              <FontBlock prefix="Tip"
                family={config.chatbotTipFontFamily || 'inherit'} size={config.chatbotTipFontSize || 12} weight={config.chatbotTipFontWeight || 600}
                onFamily={v => onChange('chatbotTipFontFamily', v)} onSize={v => onChange('chatbotTipFontSize', v)} onWeight={v => onChange('chatbotTipFontWeight', v)} />
              {config.offlineMode === 'back_in_x' && (
                <div style={{ background: '#fffbeb', padding: '10px 14px', borderRadius: 10, border: '1px solid #fde68a', marginTop: 4 }}>
                  <p style={{ fontSize: 12, color: '#92400e', lineHeight: 1.5, margin: 0 }}>
                    ⚠️ When the store is closed, the <strong>Back in X</strong> message replaces this tip. Both can't show at the same time.
                  </p>
                </div>
              )}
            </>
          )}
        </Section>
      )}
      <Section title="Availability & Schedule">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {!isGrowthPlus ? (
          <>
            <div style={{ background: '#f8fafc', padding: '16px 20px', borderRadius: 12, border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>When store is closed</span>
                    <Badge tone="attention" size="small">Growth+</Badge>
                  </div>
                  <button
                    type="button"
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '12px 14px',
                      borderRadius: 14,
                    border: '1px solid var(--brand-primary)',
                    background: 'linear-gradient(135deg, rgba(19,42,64,0.14) 0%, rgba(52,144,176,0.22) 100%)',
                    boxShadow: '0 10px 24px rgba(19, 42, 64, 0.12)',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 14,
                      cursor: 'default',
                      opacity: 0.9,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--brand-primary)', marginBottom: 4 }}>
                        Current strategy
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', lineHeight: 1.35, whiteSpace: 'normal' }}>
                        {currentOfflineStrategy.label}
                      </div>
                      <div style={{ marginTop: 4, fontSize: 12, color: '#1f5670', lineHeight: 1.5, whiteSpace: 'normal' }}>
                        {currentOfflineStrategy.description}
                      </div>
                    </div>
                    <Lock size={16} color="#1d4ed8" style={{ flexShrink: 0, marginTop: 2 }} />
                  </button>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 8, background: '#fffbeb', border: '1px solid #fcd34d' }}>
                    <Lock size={13} color="#92400e" style={{ flexShrink: 0, marginTop: 1 }} />
                    <Text as="p" variant="bodySm" tone="caution">Growth+ unlocks additional closed-store strategies and automatic scheduling. Free uses the default strategy shown above.</Text>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ background: '#fffbeb', padding: '12px 16px', borderRadius: 12, border: '1px solid #fde68a' }}>
              <p style={{ fontSize: 13, color: '#92400e', lineHeight: 1.5, margin: 0 }}>
                On Free, toggle the widget manually via{' '}
                <a href="#widget-visibility-anchor" style={{ color: '#9a3412', fontWeight: 700, textDecoration: 'underline' }}>
                  Visibility above
                </a>.
              </p>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <FieldRow label="Timezone">
              <div style={{ width: 170 }}><Select label="" labelHidden options={TIMEZONES} value={config.timezone || 'America/New_York'} onChange={v => onChange('timezone', v)} /></div>
            </FieldRow>
            <FieldRow label="Opens">
              <div style={{ width: 115 }}><Select label="" labelHidden options={HOURS} value={String(config.startHour ?? 9)} onChange={v => onChange('startHour', parseInt(v))} /></div>
            </FieldRow>
            <FieldRow label="Closes">
              <div style={{ width: 115 }}><Select label="" labelHidden options={HOURS} value={String(config.endHour ?? 17)} onChange={v => onChange('endHour', parseInt(v))} /></div>
            </FieldRow>
            <FieldRow label="Weekends">
              <div style={{ width: 170 }}><Select label="" labelHidden options={[
                { label: 'Open as normal', value: 'none' },
                { label: 'Closed on Sundays', value: 'sunday' },
                { label: 'Closed Sat & Sun', value: 'all' },
              ]} value={config.weekendBehavior || 'none'} onChange={v => onChange('weekendBehavior', v)} /></div>
            </FieldRow>

            <div style={{ background: '#f8fafc', padding: '16px 20px', borderRadius: 12, border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>When store is closed</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (showOfflineStrategyOptions) {
                        closeOfflineStrategyEditor();
                        return;
                      }
                      openOfflineStrategyEditor();
                    }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '12px 14px',
                      borderRadius: 14,
                    border: '1px solid var(--brand-primary)',
                    background: 'linear-gradient(135deg, rgba(19,42,64,0.14) 0%, rgba(52,144,176,0.22) 100%)',
                    boxShadow: '0 10px 24px rgba(19, 42, 64, 0.12)',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 14,
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--brand-primary)', marginBottom: 4 }}>
                        Current strategy
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', lineHeight: 1.35, whiteSpace: 'normal' }}>
                        {currentOfflineStrategy.label}
                      </div>
                      <div style={{ marginTop: 4, fontSize: 12, color: '#1f5670', lineHeight: 1.5, whiteSpace: 'normal' }}>
                        {currentOfflineStrategy.description}
                      </div>
                    </div>
                    <div style={{ flexShrink: 0, color: 'var(--brand-primary)', marginTop: 2, transform: showOfflineStrategyOptions ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.18s ease' }}>
                      <ChevronRight size={18} />
                    </div>
                  </button>
                  {showOfflineStrategyOptions && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <ChoiceList
                        value={draftOfflineMode}
                        onChange={v => onDraftOfflineModeChange(v)}
                        options={offlineStrategyOptions}
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                        <button
                          type="button"
                          onClick={closeOfflineStrategyEditor}
                          style={{
                            padding: '8px 14px',
                            borderRadius: 10,
                            border: '1px solid #cbd5e1',
                            background: '#ffffff',
                            color: '#475569',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={confirmOfflineStrategyEditor}
                          style={{
                            padding: '8px 14px',
                            borderRadius: 10,
                            border: '1px solid var(--brand-primary)',
                            background: 'linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 100%)',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 800,
                            cursor: 'pointer',
                            boxShadow: '0 10px 24px rgba(19, 42, 64, 0.16)',
                          }}
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {!isAgentPlus && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: 4 }}>
                    <Text as="p" variant="bodySm">"Show chatbot" offline mode requires Agent+.</Text>
                  </div>
                )}
                {(effectiveOfflineMode === 'follow_apps' || !effectiveOfflineMode) && (() => {
                  const hidden: string[] = [];
                  if (isAgentPlus && config.apps?.chatbot?.enabled) hidden.push('Chatbot');
                  const hiddenChannels = Object.entries(config.apps || {}).filter(([id, app]: [string, any]) => id !== 'chatbot' && app.enabled && app.showClosed === false).map(([id, app]: [string, any]) => app.label || id);
                  hidden.push(...hiddenChannels);
                  if (hidden.length === 0) return null;
                  return (
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 10, background: '#fff7ed', border: '1px solid #fdba74', marginTop: 4, boxShadow: '0 6px 16px rgba(249, 115, 22, 0.08)' }}>
                      <Info size={14} color="#c2410c" style={{ flexShrink: 0, marginTop: 2 }} />
                      <p style={{ fontSize: 12, color: '#475569', margin: 0, lineHeight: 1.5 }}>
                        <strong style={{ color: '#9a3412' }}>Important:</strong> When your store is closed, <strong style={{ color: '#9a3412' }}>{hidden.join(', ')}</strong> {hidden.length === 1 ? 'will not be shown' : 'will not be shown'} — channels follow their individual "Show when closed" setting.
                      </p>
                    </div>
                  );
                })()}
                {showOfflineStrategyOptions && effectiveOfflineMode === 'back_in_x' && (
                  <div style={{ background: '#ffffff', padding: '16px 20px', borderRadius: 12, border: '1px solid #e2e8f0', marginTop: 4 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <h4 style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>Availability Message</h4>
                      <FieldRow label="Message label">
                        <div style={{ width: 145 }}><TextField label="" labelHidden value={config.backInLabel || 'Back in'} onChange={v => onChange('backInLabel', v)} placeholder="Back in" autoComplete="off" /></div>
                      </FieldRow>
                      <FieldRow label="Background">
                        <ColorPicker value={config.backInBgColor || '#09090b'} onChange={v => onChange('backInBgColor', v)} resetValue="#09090b" />
                      </FieldRow>
                      <FieldRow label="Heading color">
                        <ColorPicker value={config.backInLabelColor || '#a5b4fc'} onChange={v => onChange('backInLabelColor', v)} resetValue="#a5b4fc" />
                      </FieldRow>
                      <FieldRow label="Time color">
                        <ColorPicker value={config.backInTextColor || '#f4f4f5'} onChange={v => onChange('backInTextColor', v)} resetValue="#f4f4f5" />
                      </FieldRow>
                      <FontBlock prefix="Message"
                        family={config.backInFontFamily || 'inherit'}
                        size={config.backInFontSize || 13}
                        weight={config.backInFontWeight || 700}
                        onFamily={v => onChange('backInFontFamily', v)}
                        onSize={v => onChange('backInFontSize', v)}
                        onWeight={v => onChange('backInFontWeight', v)} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ background: '#f8fafc', padding: '16px 20px', borderRadius: 12, border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <FieldRow label="☕ Lunch break"><Toggle checked={config.lunchEnabled ?? false} onChange={v => onChange('lunchEnabled', v)} /></FieldRow>
                {config.lunchEnabled && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <FieldRow label="Start"><div style={{ width: 115 }}><Select label="" labelHidden options={HOURS} value={String(config.lunchStart ?? 12)} onChange={v => onChange('lunchStart', parseInt(v))} /></div></FieldRow>
                    <FieldRow label="End"><div style={{ width: 115 }}><Select label="" labelHidden options={HOURS} value={String(config.lunchEnd ?? 13)} onChange={v => onChange('lunchEnd', parseInt(v))} /></div></FieldRow>
                  </div>
                )}
              </div>
            </div>
            <div style={{ background: '#f8fafc', padding: '16px 20px', borderRadius: 12, border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <FieldRow label="🏖️ Holiday calendar"><Toggle checked={config.holidayEnabled ?? false} onChange={v => onChange('holidayEnabled', v)} /></FieldRow>
                {config.holidayEnabled && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
                      {(config.holidays ?? []).length === 0 ? (
                        <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>No holidays configured.</p>
                      ) : (
                        (config.holidays ?? []).filter((h): h is HolidayEntry => typeof h === 'object').map((h: any) => (
                          <Badge key={h.id} tone="info">{h.name}</Badge>
                        ))
                      )}
                    </div>
                    <Button
                      fullWidth
                      onClick={() => {
                        if (setLocalHolidays && setShowHolidayModal) {
                          const current = Array.isArray(config.holidays) ? config.holidays : [];
                          setLocalHolidays([...current]);
                          setShowHolidayModal(true);
                        }
                      }}
                    >
                      Manage Holidays
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        </div>
      </Section>

    </div >
  );
}


function ContainerStyleSection({ config, onChange, isGrowthPlus, deviceMode }: { config: DockAppearanceConfig; onChange: (k: string, v: unknown) => void; isGrowthPlus: boolean; deviceMode: DeviceMode }) {
  const cStyle = config.containerGlassStyle || 'glass';
  const showDesktopControls = deviceMode === 'desktop';
  const showMobileControls = deviceMode === 'mobile';
  const cPresetDesc: Record<GlassStyle, string> = {
    glass: 'Elegant frosted glass card — blurs content behind it.',
    tinted: 'Brand glass — tinted with a custom color.',
    solid: 'Solid color card with no background blur.',
  };
  const cTint = config.containerTintColor || config.anchorColor || '#1a1a2e';
  return (
    <Section title="Container Style">
      <FieldRow label="Style" locked={!isGrowthPlus}>
        <SegmentedControl value={cStyle} onChange={v => onChange('containerGlassStyle', v)} disabled={!isGrowthPlus}
          options={[
            { label: 'Glass', value: 'glass' },
            { label: 'Tinted', value: 'tinted' },
            { label: 'Solid', value: 'solid' },
          ]} />
      </FieldRow>
      <div style={{ padding: '8px 12px', borderRadius: 8, transition: 'all 0.3s', fontSize: 12, fontWeight: 500, marginBottom: 16, background: 'rgba(255,255,255,0.7)', color: '#374151', border: '1px solid rgba(128,128,128,0.15)' }}>
        {cPresetDesc[cStyle]}
      </div>
      {(cStyle === 'tinted' || cStyle === 'solid') && (
        <FieldRow label={`${cStyle === 'solid' ? 'Solid' : 'Tint'} color`} locked={!isGrowthPlus}>
          <ColorPicker value={cTint} onChange={v => onChange('containerTintColor', v)} resetValue={config.anchorColor || '#1a1a2e'} />
        </FieldRow>
      )}
      <FieldRow label="Opacity" locked={!isGrowthPlus}>
        <div style={{ width: 180 }}><RangeSlider label="" labelHidden min={10} max={100} step={1} value={config.containerOpacity ?? 90} onChange={v => onChange('containerOpacity', v)} output /></div>
      </FieldRow>
      <FieldRow label="Apps per row">
        <SegmentedControl
          value={String(config.containerColumns ?? 3)}
          onChange={v => onChange('containerColumns', Number(v))}
          options={[
            { label: '1', value: '1' },
            { label: '2', value: '2' },
            { label: '3', value: '3' },
            { label: '4', value: '4' },
          ]}
        />
      </FieldRow>
      {showDesktopControls && (
        <>
          <FieldRow label="Show container title">
            <Toggle checked={config.dockContainerTitleEnabled === true} onChange={v => onChange('dockContainerTitleEnabled', v)} />
          </FieldRow>
          {config.dockContainerTitleEnabled === true && (
            <>
              <FieldRow label="Container title">
                <div style={{ width: 220 }}>
                  <TextField
                    label=""
                    labelHidden
                    value={config.dockContainerTitle ?? 'Get in Touch'}
                    onChange={v => onChange('dockContainerTitle', v)}
                    autoComplete="off"
                    placeholder="Get in Touch"
                  />
                </div>
              </FieldRow>
              <FieldRow label="Title alignment">
                <SegmentedControl
                  value={config.dockContainerTitleAlign || 'center'}
                  onChange={v => onChange('dockContainerTitleAlign', v)}
                  options={[
                    { label: 'Left', value: 'left' },
                    { label: 'Center', value: 'center' },
                    { label: 'Right', value: 'right' },
                  ]}
                />
              </FieldRow>
            </>
          )}
          <FieldRow label="Show channel labels">
            <Toggle checked={config.dockContainerLabels !== false} onChange={v => onChange('dockContainerLabels', v)} />
          </FieldRow>
          <FieldRow label="Show icon tooltips on hover">
            <Toggle checked={config.dockContainerTooltips !== false} onChange={v => onChange('dockContainerTooltips', v)} />
          </FieldRow>
        </>
      )}
      {showMobileControls && (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginTop: 4, marginBottom: 10 }}>
            <Text as="h3" variant="headingSm">Mobile Drawer</Text>
            <Text as="p" variant="bodySm" tone="subdued">
              These settings apply only to the mobile container drawer.
            </Text>
          </div>
          <FieldRow label="Show container title">
            <Toggle checked={config.mobileContainerTitleEnabled === true} onChange={v => onChange('mobileContainerTitleEnabled', v)} />
          </FieldRow>
          {config.mobileContainerTitleEnabled === true && (
            <>
              <FieldRow label="Container title">
                <div style={{ width: 220 }}>
                  <TextField
                    label=""
                    labelHidden
                    value={config.mobileContainerTitle ?? 'Get in Touch'}
                    onChange={v => onChange('mobileContainerTitle', v)}
                    autoComplete="off"
                    placeholder="Get in Touch"
                  />
                </div>
              </FieldRow>
              <FieldRow label="Title alignment">
                <SegmentedControl
                  value={config.mobileContainerTitleAlign || 'center'}
                  onChange={v => onChange('mobileContainerTitleAlign', v)}
                  options={[
                    { label: 'Left', value: 'left' },
                    { label: 'Center', value: 'center' },
                    { label: 'Right', value: 'right' },
                  ]}
                />
              </FieldRow>
            </>
          )}
          <FieldRow label="Show channel labels">
            <Toggle checked={config.mobileContainerLabels !== false} onChange={v => onChange('mobileContainerLabels', v)} />
          </FieldRow>
        </>
      )}
    </Section>
  );
}

function DockStylePanel({ config, onChange, isGrowthPlus, isAgentPlus, deviceMode, onLaunchStyleChange }: { config: DockAppearanceConfig; onChange: (k: string, v: unknown) => void; isGrowthPlus: boolean; isAgentPlus: boolean; deviceMode: DeviceMode; onLaunchStyleChange?: (value: 'dock' | 'container' | 'chat_first') => void }) {
  const presetDesc: Record<GlassStyle, string> = {
    glass: 'Elegant frosted glass preset.',
    tinted: 'Brand glass — tinted with a custom color.',
    solid: 'Solid color preset with no background blur.',
  };
  const resolvedTint = config.dockTintColor || config.anchorColor || '#000000';
  const canUseChatFirst = isAgentPlus;
  const launchStyle = resolveEditorLaunchStyle(config.launchStyle, isGrowthPlus, isAgentPlus);
  const isContainerMode = launchStyle === 'container';
  const isChatFirstMode = launchStyle === 'chat_first';
  const visibleBeforeOverflow = config.dockMaxVisibleApps ?? 2;
  const enabledDockAppCount = Object.entries(config.apps || {})
    .filter(([id, app]: [string, any]) => id !== 'chatbot' && app.enabled !== false)
    .length;
  const hiddenDockAppCount = Math.max(0, enabledDockAppCount - visibleBeforeOverflow);
  const launchStyleMeta = [
    {
      label: 'Dock',
      value: 'dock',
      flow: 'Button -> Dock -> Chat / channels',
      description: 'A compact row of channels and chat slides out from the launcher.',
    },
    {
      label: 'Container',
      value: 'container',
      flow: 'Button -> Drawer -> Chat / channels',
      description: 'A fuller support hub opens above the button and uses the mobile drawer on phones.',
    },
    {
      label: 'Chat-first',
      value: 'chat_first',
      flow: 'Button -> Chat -> Support options',
      description: 'Open the assistant right away and surface contact channels inside chat only when needed.',
    },
  ] as const;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Section title="Launch Style">
        <FieldRow label="Style" locked={!isGrowthPlus}>
          <SegmentedControl
            value={isGrowthPlus ? launchStyle : 'dock'}
            onChange={v => {
              const nextValue = v as 'dock' | 'container' | 'chat_first';
              onChange('launchStyle', nextValue);
              onLaunchStyleChange?.(nextValue);
            }}
            disabled={!isGrowthPlus}
            options={[
              { label: 'Dock Bar', value: 'dock' },
              { label: 'Container', value: 'container' },
              ...(canUseChatFirst ? [{ label: 'Chat-first', value: 'chat_first' }] : []),
            ]}
          />
        </FieldRow>
        <p style={{ fontSize: 13, color: '#64748b', margin: 0, marginTop: 4 }}>
          {isChatFirstMode && isGrowthPlus
            ? 'The launcher skips the dock or drawer and opens chat immediately. Enabled channels stay available from a Support entry inside the chat window.'
            : isContainerMode && isGrowthPlus
            ? 'A glass popup card opens above the button containing all channels and the chatbot entry. On mobile, the existing drawer is used instead.'
            : 'Channels slide out in a horizontal pill from the main button. On mobile, a full-screen drawer opens.'}
        </p>
        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          {launchStyleMeta.map((item) => {
            const selected = item.value === launchStyle;
            return (
              <div
                key={item.value}
                style={{
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: selected ? '1px solid var(--theme-action-accent)' : '1px solid #e2e8f0',
                  background: selected ? 'linear-gradient(180deg, rgba(52,144,176,0.12) 0%, rgba(255,255,255,0.96) 100%)' : '#ffffff',
                  boxShadow: selected ? '0 8px 24px rgba(52, 144, 176, 0.12)' : '0 2px 8px rgba(15,23,42,0.03)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{item.label}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: selected ? 'var(--theme-action-accent)' : '#64748b', letterSpacing: '0.02em' }}>{item.flow}</span>
                </div>
                <p style={{ margin: 0, fontSize: 12, color: '#64748b', lineHeight: 1.45 }}>
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>
        {isChatFirstMode && config.apps?.chatbot?.enabled === false && (
          <div style={{ marginTop: 12, background: '#fffbeb', padding: '10px 12px', borderRadius: 10, border: '1px solid #fde68a' }}>
            <p style={{ margin: 0, fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
              Enable the chatbot to use Chat-first. Otherwise the launcher has no direct chat destination.
            </p>
          </div>
        )}
        {!canUseChatFirst && (
          <div style={{ marginTop: 12, background: '#f8fafc', padding: '10px 12px', borderRadius: 10, border: '1px solid #e2e8f0' }}>
            <p style={{ margin: 0, fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
              Chat-first requires Agent+. Lower tiers fall back to the standard dock flow.
            </p>
          </div>
        )}
      </Section>

      {isChatFirstMode ? (
        <Section title="Chat-first Flow">
          <p style={{ fontSize: 13, color: '#64748b', margin: 0, lineHeight: 1.55 }}>
            Chat-first keeps the launcher tidy by opening the assistant immediately. Dock and container surface styling is skipped in this mode. Enabled channels are still reused, but they show up as contact handoff options inside the chat window when the customer taps <strong>Support</strong> or a <strong>Contact Support</strong> suggestion.
          </p>
        </Section>
      ) : isContainerMode ? (
        <ContainerStyleSection config={config} onChange={onChange} isGrowthPlus={isGrowthPlus} deviceMode={deviceMode} />
      ) : (
        <>
          <Section title="Dock Style">
            <FieldRow label="Style" locked={!isGrowthPlus}>
              <SegmentedControl value={config.dockGlassStyle || 'solid'} onChange={v => onChange('dockGlassStyle', v)} disabled={!isGrowthPlus}
                options={[
                  { label: 'Glass', value: 'glass' },
                  { label: 'Tinted', value: 'tinted' },
                  { label: 'Solid', value: 'solid' }
                ]} />
            </FieldRow>
            <div style={{
              padding: '8px 12px', borderRadius: 8, transition: 'all 0.3s', fontSize: 12, fontWeight: 500,
              background: config.dockGlassStyle === 'tinted' ? resolvedTint : (config.dockGlassStyle === 'solid' ? `rgba(${parseInt(resolvedTint.slice(1, 3), 16)}, ${parseInt(resolvedTint.slice(3, 5), 16)}, ${parseInt(resolvedTint.slice(5, 7), 16)}, 0.8)` : 'rgba(255,255,255,0.8)'),
              color: (config.dockGlassStyle === 'tinted' || config.dockGlassStyle === 'solid') ? '#ffffff' : '#374151',
              border: '1px solid rgba(128,128,128,0.15)',
              marginBottom: 16,
            }}>
              {presetDesc[config.dockGlassStyle || 'solid']}
            </div>
            {((config.dockGlassStyle || 'solid') === 'tinted' || (config.dockGlassStyle || 'solid') === 'solid') && (
              <FieldRow label={`${(config.dockGlassStyle || 'solid') === 'solid' ? 'Solid' : 'Tint'} color`} locked={!isGrowthPlus}>
                <ColorPicker value={resolvedTint} onChange={v => onChange('dockTintColor', v)} resetValue={config.anchorColor || '#000000'} />
              </FieldRow>
            )}
            <FieldRow label="Opacity" locked={!isGrowthPlus}>
              <div style={{ width: 180 }}><RangeSlider label="" labelHidden min={0} max={100} step={1} value={config.dockOpacity ?? 100} onChange={v => onChange('dockOpacity', v)} output /></div>
            </FieldRow>
            {!isGrowthPlus && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 8, background: '#fffbeb', border: '1px solid #fcd34d' }}>
                <Lock size={13} color="#92400e" style={{ flexShrink: 0, marginTop: 1 }} />
                <Text as="p" variant="bodySm" tone="caution">Glass and Tinted presets are Growth+ features. On free, the button and dock are solid black.</Text>
              </div>
            )}
          </Section>

          <Section title="Overflow Button">
            {deviceMode === 'desktop' ? (
              <>
                <FieldRow label="Apps shown before +N" locked={!isGrowthPlus}>
                  <div style={{ width: 180 }}><RangeSlider label="" labelHidden min={2} max={8} step={1} value={config.dockMaxVisibleApps ?? 2} onChange={v => onChange('dockMaxVisibleApps', v)} output /></div>
                </FieldRow>
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start', gap: 8, padding: '6px 10px', borderRadius: 9999, background: hiddenDockAppCount > 0 ? '#eef2ff' : '#f8fafc', border: '1px solid #e2e8f0', color: '#334155', fontSize: 12, fontWeight: 700 }}>
                    <span>Current preview</span>
                    <span style={{ padding: '2px 7px', borderRadius: 9999, background: hiddenDockAppCount > 0 ? '#312e81' : '#e2e8f0', color: hiddenDockAppCount > 0 ? '#ffffff' : '#64748b' }}>
                      {hiddenDockAppCount > 0 ? `+${hiddenDockAppCount}` : 'No +N'}
                    </span>
                  </div>
                  <p style={{ fontSize: 13, color: '#64748b', margin: 0, lineHeight: 1.5 }}>
                    Show the first {visibleBeforeOverflow} app{visibleBeforeOverflow === 1 ? '' : 's'} directly in the desktop dock. If more apps are enabled, the rest collapse into a compact +N button, where N is the number of hidden apps. With your current desktop setup, {enabledDockAppCount} app{enabledDockAppCount === 1 ? ' is' : 's are'}  enabled, so {hiddenDockAppCount > 0 ? `${hiddenDockAppCount} will be hidden behind +${hiddenDockAppCount}` : 'no overflow button is needed'}.
                  </p>
                  <p style={{ fontSize: 12, color: '#94a3b8', margin: 0, lineHeight: 1.45 }}>
                    Mobile is handled automatically and always shows up to 2 apps before +N so the dock fits the smaller screen.
                  </p>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <Lock size={13} color="#64748b" style={{ flexShrink: 0, marginTop: 2 }} />
                  <Text as="p" variant="bodySm" tone="subdued">
                    Mobile is capped at 2 apps before +N so the dock stays compact.
                  </Text>
                </div>
                <p style={{ fontSize: 13, color: '#64748b', margin: 0, lineHeight: 1.5 }}>
                  If more than 2 apps are enabled, mobile groups the rest under +N automatically. Switch to Desktop preview to change the desktop overflow setting.
                </p>
              </div>
            )}
          </Section>

          {deviceMode === 'desktop' && (
            <Section title="Tooltips">
              <FieldRow label="Show icon tooltips on hover">
                <Toggle checked={config.dockTooltipsEnabled !== false} onChange={v => onChange('dockTooltipsEnabled', v)} />
              </FieldRow>
              <div style={{ marginTop: 8 }}>
                <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
                  Displays the channel name when hovering over an icon on desktop.
                </p>
              </div>
            </Section>
          )}
        </>
      )}

      {isAgentPlus && (
        <Section title="Chatbot">
          {(() => {
            const apps = config.apps || {};
            const setApp = (key: string, val: unknown) =>
              onChange('apps', { ...apps, chatbot: { ...(apps.chatbot || {}), [key]: val } });
            const enabled = apps.chatbot?.enabled !== false;
            return (
              <>
                <FieldRow label="Enable chatbot">
                  <Toggle checked={enabled} onChange={v => setApp('enabled', v)} />
                </FieldRow>
                {enabled && (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 16px', marginTop: 4, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <FieldRow label="Show when store is open">
                      <Toggle checked={apps.chatbot?.showOpen !== false} onChange={v => setApp('showOpen', v)} />
                    </FieldRow>
                    <FieldRow label="Show when store is closed">
                      <Toggle checked={apps.chatbot?.showClosed !== false} onChange={v => setApp('showClosed', v)} />
                    </FieldRow>
                    <FieldRow label="Label text">
                      <div style={{ width: 155 }}><TextField label="" labelHidden value={apps.chatbot?.label || ''} onChange={v => setApp('label', v)} autoComplete="off" placeholder="Ask Assistant…" /></div>
                    </FieldRow>
                    <FieldRow label="Avatar URL">
                      <div style={{ width: 155 }}><TextField label="" labelHidden value={config.chatbotEntryAvatar || ''} onChange={v => onChange('chatbotEntryAvatar', v)} autoComplete="off" placeholder="https://…" /></div>
                    </FieldRow>
                  </div>
                )}
              </>
            );
          })()}
        </Section>
      )}
    </div>
  );
}

function resolveEditorLaunchStyle(
  launchStyle: DockAppearanceConfig['launchStyle'],
  isGrowthPlus: boolean,
  isAgentPlus: boolean,
): 'dock' | 'container' | 'chat_first' {
  const requestedStyle = launchStyle || 'dock';
  if (!isGrowthPlus) return 'dock';
  if (!isAgentPlus && requestedStyle === 'chat_first') return 'dock';
  return requestedStyle;
}


function ChannelsPanel({ config, onChange, onSelectChannel }: {
  config: DockAppearanceConfig; onChange: (k: string, v: unknown) => void;
  onSelectChannel: (id: string) => void;
}) {
  const apps = config.apps || {};
  const [search, setSearch] = useState('');


  const setupChannel = (id: string) => {
    // Ensure the channel has an order if it doesn't yet, so settings are saved correctly
    if (!apps[id]?.order) {
      const maxOrder = Math.max(...Object.values(apps).map(a => a.order ?? -1), -1);
      onChange('apps', { ...apps, [id]: { ...(apps[id] || {}), order: maxOrder + 1, enabled: false } });
    }
    onSelectChannel(id);
  };

  const enabledEntries = useMemo(() => {
    return Object.entries(apps)
      .filter(([id, app]) => CHANNEL_KEYS.includes(id) && app.enabled !== false)
      .sort(([, a], [, b]) => (a.order ?? Infinity) - (b.order ?? Infinity))
      .filter(([id, app]) => {
        const meta = CHANNEL_META[id.toLowerCase()];
        const label = app.label || meta?.label || id;
        return label.toLowerCase().includes(search.toLowerCase());
      });
  }, [apps, search]);

  const handleReorder = (newOrderIds: string[]) => {
    const updated = { ...apps };
    newOrderIds.forEach((id, i) => { updated[id] = { ...(updated[id] || {}), order: i }; });
    onChange('apps', updated);
  };

  const availableMeta = useMemo(() => {
    const activeLower = new Set(enabledEntries.map(([id]) => id.toLowerCase()));
    return CHANNEL_KEYS.filter(k => !activeLower.has(k)).reduce((active, key) => {
      active[key as keyof typeof CHANNEL_META] = CHANNEL_META[key];
      return active;
    }, {} as Record<string, any>);
  }, [config.apps, enabledEntries]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {config.launchStyle === 'chat_first' && (config.planTier === 'rep' || config.planTier === 'vea' || config.planTier === 'agent') && (
        <div style={{ background: 'linear-gradient(180deg, rgba(52,144,176,0.10) 0%, rgba(255,255,255,0.96) 100%)', border: '1px solid rgba(52,144,176,0.22)', borderRadius: 12, padding: '12px 14px' }}>
          <p style={{ margin: 0, fontSize: 12, color: '#33546a', lineHeight: 1.5 }}>
            In <strong>Chat-first</strong>, enabled channels appear inside chat as support handoff options when the customer taps <strong>Support</strong> or a <strong>Contact Support</strong> suggestion.
          </p>
        </div>
      )}
      {/* Search Input */}
      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '4px 12px', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 2px 6px rgba(0,0,0,0.02)' }}>
        <Search size={16} color="#94a3b8" />
        <input
          placeholder="Search channels..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', fontSize: 13, padding: '8px 0', color: '#334155' }}
        />
      </div>

      <Section title="Active Channels">
        <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, margin: '0 0 12px 0' }}>
          To edit an item, click anywhere on the row to open the settings panel for that channel. To disable an app, go into its settings.
        </p>
        {enabledEntries.length === 0 ? (
          <p style={{ fontSize: 13, color: '#64748b', textAlign: 'center', padding: '16px 0', background: '#f8fafc', borderRadius: 12, border: '1px dashed #cbd5e1', margin: 0 }}>No apps enabled.</p>
        ) : (
          <Reorder.Group axis="y" values={enabledEntries.map(([id]) => id)} onReorder={handleReorder} style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {enabledEntries.map(([id, app]) => {
              if (id === 'chatbot') return null; // Chatbot is configured separately in the Chatbot section
              const meta = CHANNEL_META[id.toLowerCase()];
              const RealIcon = meta?.icon;
              return (
                <Reorder.Item key={id} value={id} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, display: 'flex', alignItems: 'center', padding: '10px 12px', gap: 12, cursor: 'grab', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                  <GripVertical size={16} color="#cbd5e1" style={{ cursor: 'grab' }} />
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: app.color || meta?.color || '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative' }}>
                    {RealIcon && <RealIcon size={16} color="white" />}
                    {(!app.value || !app.value.trim()) && (
                      <div title={`${meta?.fieldLabel || 'Details'} not set`} style={{ position: 'absolute', top: -4, right: -4, width: 14, height: 14, background: '#ef4444', borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 900, border: '2px solid white' }}>!</div>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => onSelectChannel(id)}>
                    <h4 style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', margin: '0 0 2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{app.label || meta?.label}</h4>
                    <p style={{ fontSize: 11, color: '#64748b', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{app.value || `${meta?.fieldLabel || 'Details'} not configured`}</p>
                  </div>
                </Reorder.Item>
              );
            })}
          </Reorder.Group>
        )}
      </Section>

      {Object.keys(availableMeta).length > 0 && (
        <Section title="Available Channels">
          <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, margin: '0 0 12px 0' }}>
            Click a card to configure its details. You can only enable a channel once its details are set.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
            {Object.entries(availableMeta)
              .filter(([_, m]) => m.label.toLowerCase().includes(search.toLowerCase()))
              .map(([id, meta]) => {
                const RealIcon = meta.icon;
                return (
                  <div key={id} onClick={() => setupChannel(id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s' }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: meta.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <RealIcon size={14} color="white" />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#334155' }}>{meta.label}</span>
                  </div>
                );
              })}
          </div>
        </Section>
      )}
    </div>
  );
}

function ChannelItemPanel({ config, onChange, channelId, isGrowthPlus, onBack }: {
  config: DockAppearanceConfig; onChange: (k: string, v: unknown) => void;
  channelId: string; isGrowthPlus: boolean; onBack: () => void;
}) {
  const apps = config.apps || {};
  const app = apps[channelId] || {};
  const meta = CHANNEL_META[channelId];
  const setApp = (key: string, val: unknown) => {
    const nextApp = { ...app, [key]: val };
    if (key === 'value' && typeof val === 'string' && val.trim()) {
      nextApp.enabled = true;
    }
    onChange('apps', { ...apps, [channelId]: nextApp });
  };

  if (!meta) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Back Button */}
      <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer', color: '#475569', fontSize: 13, fontWeight: 500, padding: '8px 12px', borderRadius: 10, transition: 'all 0.2s', width: 'fit-content' }}>
        <ChevronLeft size={16} /> Back to channels
      </button>

      <Section title={meta.label}>
        <FieldRow label="Enabled">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Toggle
              checked={app.enabled === true}
              onChange={v => {
                setApp('enabled', v);
                if (!v) onBack(); // Auto-return to list when disabling
              }}
              disabled={!app.value || !app.value.trim()}
            />
            {(!app.value || !app.value.trim()) && (
              <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 500 }}>Add {meta.fieldLabel.toLowerCase()} to enable</span>
            )}
          </div>
        </FieldRow>
        <FieldRow label={meta.fieldLabel}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: 155 }}>
            <TextField label="" labelHidden value={app.value || ''} onChange={v => setApp('value', v)} autoComplete="off" placeholder={meta.placeholder} />
            <p style={{ margin: 0, fontSize: 11, color: '#64748b', lineHeight: 1.4 }}>{meta.hint}</p>
          </div>
        </FieldRow>
      </Section>

      <Section title="Visibility">
        <FieldRow label="Show when store is open">
          <Toggle checked={app.showOpen !== false} onChange={v => setApp('showOpen', v)} />
        </FieldRow>
        <FieldRow label="Show when store is closed">
          <Toggle checked={app.showClosed !== false} onChange={v => setApp('showClosed', v)} />
        </FieldRow>
        <FieldRow label="Show on mobile" locked={!isGrowthPlus}>
          <Toggle checked={app.showOnMobile !== false} onChange={v => setApp('showOnMobile', v)} />
        </FieldRow>
        <FieldRow label="Show on desktop" locked={!isGrowthPlus}>
          <Toggle checked={app.showOnDesktop !== false} onChange={v => setApp('showOnDesktop', v)} />
        </FieldRow>
      </Section>

      <Section title="Appearance">
        <FieldRow label="Tooltip label" locked={!isGrowthPlus}>
          <div style={{ width: 155 }}><TextField label="" labelHidden value={app.label || ''} onChange={v => setApp('label', v)} autoComplete="off" placeholder={meta.label} /></div>
        </FieldRow>
        <p style={{ fontSize: 11, color: '#64748b', marginTop: -4, marginBottom: 8 }}>Shown on hover on desktop. Defaults to the channel name.</p>
        <FieldRow label="Icon color" locked={!isGrowthPlus}>
          <ColorPicker value={app.color || meta.color} onChange={v => setApp('color', v)} resetValue={meta.color} />
        </FieldRow>
      </Section>
    </div>
  );
}

function ChatHeaderPanel({ config, onChange, isGrowthPlus }: { config: DockAppearanceConfig; onChange: (k: string, v: unknown) => void; isGrowthPlus: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Section title="Content">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <TypographyGroup title="Identity">
            <FieldRow label="Bot name">
              <div style={{ width: 155 }}><TextField label="" labelHidden value={config.botName || ''} onChange={v => onChange('botName', v)} autoComplete="off" placeholder="AI Assistant" /></div>
            </FieldRow>
            <FieldRow label="Tagline" locked={!isGrowthPlus}>
              <div style={{ width: 155 }}><TextField label="" labelHidden value={config.chatbotHeaderTagline || ''} onChange={v => onChange('chatbotHeaderTagline', v)} autoComplete="off" placeholder="Online" /></div>
            </FieldRow>
            <FieldRow label="Avatar URL" locked={!isGrowthPlus}>
              <div style={{ width: 155 }}><TextField label="" labelHidden value={config.chatbotAvatar || ''} onChange={v => onChange('chatbotAvatar', v)} autoComplete="off" placeholder="https://…" /></div>
            </FieldRow>
            <FieldRow label="Show header avatar" locked={!isGrowthPlus}>
              <Toggle checked={config.chatbotHeaderAvatarEnabled !== false} onChange={v => onChange('chatbotHeaderAvatarEnabled', v)} />
            </FieldRow>
            <FieldRow label="Show header status dot" locked={!isGrowthPlus}>
              <Toggle checked={config.chatbotHeaderStatusDotEnabled !== false} onChange={v => onChange('chatbotHeaderStatusDotEnabled', v)} />
            </FieldRow>
          </TypographyGroup>
          <TypographyGroup title="Messages">
            <FieldRow label="Welcome message">
              <div style={{ width: 155 }}><TextField label="" labelHidden value={config.welcomeMsg || ''} onChange={v => onChange('welcomeMsg', v)} autoComplete="off" multiline={2} placeholder="Hi! How can I help you?" /></div>
            </FieldRow>
            <FieldRow label="Quick suggestions">
              <div style={{ width: 155 }}><TextField label="" labelHidden value={config.initialSuggestions || ''} onChange={v => onChange('initialSuggestions', v)} autoComplete="off" placeholder="Comma-separated" multiline={2} /></div>
            </FieldRow>
          </TypographyGroup>
        </div>
      </Section>
    </div>
  );
}

function ChatBodyPanel({ config, onChange, isGrowthPlus }: { config: DockAppearanceConfig; onChange: (k: string, v: unknown) => void; isGrowthPlus: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Section title="Colors">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <TypographyGroup title="Header">
            <FieldRow label="Header color" locked={!isGrowthPlus}>
              <ColorPicker value={config.botColor || config.anchorColor || '#000000'} onChange={v => onChange('botColor', v)} />
            </FieldRow>
            <FieldRow label="Header fill" locked={!isGrowthPlus}>
              <div style={{ width: 155 }}>
                <Select
                  label=""
                  labelHidden
                  options={[{ label: 'Solid', value: 'solid' }, { label: 'Gradient', value: 'gradient' }]}
                  value={config.chatbotHeaderStyle || 'solid'}
                  onChange={v => onChange('chatbotHeaderStyle', v)}
                />
              </div>
            </FieldRow>
            <FieldRow label="Header text" locked={!isGrowthPlus}>
              <ColorPicker value={config.chatbotHeaderTextColor || '#ffffff'} onChange={v => onChange('chatbotHeaderTextColor', v)} />
            </FieldRow>
          </TypographyGroup>
          <TypographyGroup title="Window">
            <FieldRow label="Background" locked={!isGrowthPlus}>
              <ColorPicker value={config.chatbotContainerColor || '#ffffff'} onChange={v => onChange('chatbotContainerColor', v)} />
            </FieldRow>
            <FieldRow label="Opacity" locked={!isGrowthPlus}>
              <div style={{ width: 180 }}><RangeSlider label="" labelHidden min={50} max={100} step={1} value={config.chatbotContainerOpacity ?? 100} onChange={v => onChange('chatbotContainerOpacity', v)} output /></div>
            </FieldRow>
          </TypographyGroup>
        </div>
      </Section>

      <Section title="Typography">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <TypographyGroup title="Header">
            <FontBlock prefix=""
              family={config.chatbotHeaderFontFamily || 'inherit'} size={config.chatbotHeaderFontSize || 14} weight={config.chatbotHeaderFontWeight || 700}
              onFamily={v => onChange('chatbotHeaderFontFamily', v)} onSize={v => onChange('chatbotHeaderFontSize', v)} onWeight={v => onChange('chatbotHeaderFontWeight', v)}
              locked={!isGrowthPlus} />
          </TypographyGroup>
          <TypographyGroup title="Chat Bubbles">
            <FontBlock prefix=""
              family={config.chatbotBodyFontFamily || 'inherit'} size={config.chatbotBodyFontSize || 14} weight={config.chatbotBodyFontWeight || 400}
              onFamily={v => onChange('chatbotBodyFontFamily', v)} onSize={v => onChange('chatbotBodyFontSize', v)} onWeight={v => onChange('chatbotBodyFontWeight', v)}
              locked={!isGrowthPlus} />
          </TypographyGroup>
          <TypographyGroup title="Suggestion Pills">
            <FontBlock prefix=""
              family={config.chatbotSuggestionFontFamily || config.chatbotBodyFontFamily || 'inherit'} size={config.chatbotSuggestionFontSize || config.chatbotBodyFontSize || 14} weight={config.chatbotSuggestionFontWeight || 400}
              onFamily={v => onChange('chatbotSuggestionFontFamily', v)} onSize={v => onChange('chatbotSuggestionFontSize', v)} onWeight={v => onChange('chatbotSuggestionFontWeight', v)}
              locked={!isGrowthPlus} />
          </TypographyGroup>
        </div>
      </Section>
    </div>
  );
}

// ─── Preview Components ───────────────────────────────────────────────────────

function MockWebpage() {
  return (
    <>
      <style>{`
        .mock-webpage-scroll::-webkit-scrollbar {
          width: 4px;
        }
        .mock-webpage-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .mock-webpage-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(15, 23, 42, 0.15);
          border-radius: 10px;
        }
        .mock-webpage-scroll::-webkit-scrollbar-thumb:hover {
          background-color: rgba(15, 23, 42, 0.25);
        }
      `}</style>
      <div className="mock-webpage-scroll" style={{ position: 'absolute', inset: 0, overflowY: 'auto', pointerEvents: 'none', background: '#2A2D3E', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
        {/* Navbar */}
        <div style={{ height: 60, background: '#1e202b', display: 'flex', alignItems: 'center', padding: '0 24px', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ffffff' }}>
            <ReachLogo size="small" showText={false} color="#ffffff" detailColor="#4B0082" />
            <span style={{ fontSize: 18, fontWeight: 900, letterSpacing: '-0.02em', marginLeft: -4, display: 'flex', alignItems: 'baseline' }}>
              REACH<span style={{ color: '#D81B60', marginLeft: 2, fontSize: 24, lineHeight: 0.5 }}>.</span>
            </span>
          </div>
          <div style={{ display: 'flex', gap: 20 }}>
            <div style={{ width: 48, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.15)' }} />
            <div style={{ width: 48, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.15)' }} />
          </div>
        </div>

        {/* Hero Section */}
        <div style={{ background: 'linear-gradient(135deg, #434550ff 0%, #3b3c45ff 100%)', padding: '60px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', padding: '6px 16px', borderRadius: 9999, fontSize: 13, fontWeight: 700, color: '#D81B60', marginBottom: 20 }}>
            Preview Mode Active
          </div>
          <h1 style={{ fontSize: 36, fontWeight: 800, color: '#ffffff', lineHeight: 1.1, marginBottom: 16, letterSpacing: '-0.04em', maxWidth: 400 }}>
            Connect with your customers instantly
          </h1>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.6)', maxWidth: 360, lineHeight: 1.5, marginBottom: 32 }}>
            You're currently editing the appearance of your Reach widget. Interact with it directly to preview
            animations and styles before pushing live!
          </p>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #D81B60 0%, #4B0082 100%)', borderRadius: 9999, color: '#ffffff', fontSize: 14, fontWeight: 800, letterSpacing: '-0.02em', boxShadow: '0 10px 30px rgba(216, 27, 96, 0.3)', position: 'relative', overflow: 'hidden' }}>
              Setup Instructions
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(115deg, transparent 45%, rgba(255,255,255,0.15) 50%, transparent 55%)' }} />
            </div>
            <div style={{ padding: '12px 24px', background: '#4E5575', borderRadius: 9999, color: '#ffffff', fontSize: 14, fontWeight: 700 }}>
              Read Docs
            </div>
          </div>
        </div>

        {/* Feature Grids */}
        <div style={{ padding: '0 24px 40px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
          {[
            { icon: <MessageCircle size={20} color="#ffffff" />, bg: '#3b82f6', title: 'Unified Inbox', desc: 'All channels in one place' },
            { icon: <Bot size={20} color="#ffffff" />, bg: '#8b5cf6', title: 'Auto-Replies', desc: 'Instant support coverage' },
            { icon: <Lock size={20} color="#ffffff" />, bg: '#10b981', title: 'Secure Chats', desc: 'End-to-end encryption' }
          ].map((feat, i) => (
            <div key={i} style={{ padding: 20, background: '#ffffff', borderRadius: 16, border: '1px solid #f1f5f9', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: feat.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12, boxShadow: `0 4px 12px ${feat.bg}40` }}>
                {feat.icon}
              </div>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{feat.title}</h3>
              <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.4 }}>{feat.desc}</p>
            </div>
          ))}
        </div>

        {/* Mock Dense UI / Dashboard Table (for overlay testing) */}
        <div style={{ padding: '0 24px 40px' }}>
          <div style={{ background: '#ffffff', border: '1px solid #f1f5f9', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Recent Activity</h3>
              <div style={{ width: 80, height: 28, background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }} />
            </div>
            <div style={{ padding: '0' }}>
              {[
                { status: '#10b981', userBg: '#dcfce7', user: 120, text: 'Resolved support query regarding order #4592', date: 'Just now' },
                { status: '#f59e0b', userBg: '#fef3c7', user: 90, text: 'Waiting for customer response on pricing tier', date: '2m ago' },
                { status: '#3b82f6', userBg: '#dbeafe', user: 150, text: 'New inbound conversation via WhatsApp', date: '15m ago' },
                { status: '#0f172a', userBg: '#e2e8f0', user: 80, text: 'System auto-reply triggered for opening hours', date: '1h ago' },
                { status: '#64748b', userBg: '#f1f5f9', user: 110, text: 'Conversation marked as closed by Sarah', date: '2h ago' },
              ].map((row, i) => (
                <div key={i} style={{ padding: '16px 20px', borderBottom: i < 4 ? '1px solid #f1f5f9' : 'none', display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: row.status, flexShrink: 0, boxShadow: `0 0 0 3px ${row.status}20` }} />
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: row.userBg, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ width: row.user, height: 6, borderRadius: 3, background: row.status === '#0f172a' ? '#64748b' : '#0f172a', marginBottom: 8 }} />
                    <div style={{ width: row.text.length * 4, height: 5, borderRadius: 3, background: '#cbd5e1' }} />
                  </div>
                  <div style={{ width: 45, height: 4, borderRadius: 2, background: '#e2e8f0', flexShrink: 0 }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom padding so widget doesn't cover content fully */}
    </>
  );
}

const DockPreview = ({
  config, dockState, setDockState, activeZone, onZoneClick, isMobile, selectedChannelId, setSelectedChannelId,
  isDragging, dragPos, onPointerDown, canvasW, canvasH,
  isDragMode, onCommit, onCancel, showTapHint, setShowTapHint, effectivePosition, isEvaluatingOfflineStrategy
}: {
  config: DockAppearanceConfig; dockState: DockState; setDockState: (s: DockState) => void;
  activeZone: ZoneId; onZoneClick: (z: ZoneId) => void;
  isMobile: boolean; selectedChannelId: string | null; setSelectedChannelId: (id: string | null) => void;
  isDragging?: boolean;
  dragPos?: { x: number; y: number };
  onPointerDown?: (e: React.PointerEvent) => void;
  canvasW?: number;
  canvasH?: number;
  isDragMode?: boolean;
  onCommit?: () => void;
  onCancel?: () => void;
  showTapHint: boolean;
  setShowTapHint: (v: boolean) => void;
  effectivePosition?: 'left' | 'right';
  isEvaluatingOfflineStrategy?: boolean;
}) => {
  const rawDesktopSize = config.desktopButtonSize || 60;
  const rawMobileSize = config.mobileButtonSize || 52;

  const btnSizeDesktopNum = typeof rawDesktopSize === 'number' ? rawDesktopSize : 60;
  const btnSizeMobileNum = typeof rawMobileSize === 'number' ? rawMobileSize : 52;

  const btnSize = isMobile ? btnSizeMobileNum : btnSizeDesktopNum
  const radiusRaw = isMobile ? config.mobileRadius : config.buttonRadius;
  const btnRadius = typeof radiusRaw === 'number' ? radiusRaw : (btnSize / 2);
  const iconSize = Math.round(btnSize * 0.44);
  const anchorLayout = getPreviewAnchorLayout({
    config,
    isMobile,
    canvasWidth: canvasW,
    canvasHeight: canvasH,
    dragPos,
    isDragging,
    effectivePosition,
  });
  const isLeft = anchorLayout.isLeft;
  const isExpanded = dockState === 'expanded' || dockState === 'allApps';
  const maxVis = isMobile ? 2 : (config.dockMaxVisibleApps ?? 4);
  const isFree = (config.planTier || 'free') === 'free';
  const activeColor = isFree ? '#000000' : (config.anchorColor || '#000000');

  const apps = config.apps || {};
  const tintColor = isFree ? activeColor : (config.dockTintColor || config.anchorColor || '#000000');
  const glass = buildGlassTokens(isFree ? 'solid' : (config.dockGlassStyle || 'solid'), isFree ? 100 : (config.dockOpacity ?? 100), tintColor);

  const enabledApps = useMemo(() =>
    Object.entries(apps)
      .filter(([id, s]: [string, any]) => id !== 'chatbot' && s.enabled !== false && CHANNEL_META[id.toLowerCase()])
      .sort((a, b) => (a[1].order ?? 0) - (b[1].order ?? 0)),
    [apps]);

  const isChatOpen = dockState === 'chat';

  const scrollRef = useRef<HTMLDivElement>(null);
  const [hoveredApp, setHoveredApp] = useState<string | null>(null);
  const [hoveredZone, setHoveredZone] = useState<ZoneId>(null);
  const [hoveredStatusDot, setHoveredStatusDot] = useState(false);
  const iconRefs = useRef<Map<string, HTMLElement>>(new Map());

  const scrollLeftBtn = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    scrollRef.current?.scrollBy({ left: -(scrollAreaW + iconGap), behavior: 'smooth' });
  };
  const scrollRightBtn = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    scrollRef.current?.scrollBy({ left: scrollAreaW + iconGap, behavior: 'smooth' });
  };
  const handleWheel = (e: React.WheelEvent) => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft += e.deltaY;
    }
  };

  useEffect(() => {
    if (dockState !== 'closed') setShowTapHint(false);
  }, [dockState]);

  useEffect(() => {
    if (dockState === 'closed' || isDragging) setHoveredApp(null);
  }, [dockState, isDragging]);

  useEffect(() => {
    if (dockState === 'closed' || isDragging) setHoveredZone(null);
  }, [dockState, isDragging]);
  useEffect(() => {
    if (dockState !== 'closed' || isDragging) setHoveredStatusDot(false);
  }, [dockState, isDragging]);
  const isAISalesRepPlus = config.planTier === 'rep' || config.planTier === 'vea' || config.planTier === 'agent';
  const showChatZone = apps.chatbot?.enabled !== false && isAISalesRepPlus;
  const showAppZone = enabledApps.length > 0;
  const previewSchedule = useMemo(() => ({
    isEnabled: config.isEnabled ?? true,
    startHour: config.startHour ?? 9,
    endHour: config.endHour ?? 17,
    timezone: config.timezone || 'America/New_York',
    weekendBehavior: config.weekendBehavior ?? 'sunday',
    lunchEnabled: config.lunchEnabled ?? false,
    lunchStart: config.lunchStart ?? 12,
    lunchEnd: config.lunchEnd ?? 13,
    holidayEnabled: config.holidayEnabled ?? false,
    holidays: config.holidays ?? [],
  }) as ScheduleConfig, [
    config.isEnabled,
    config.startHour,
    config.endHour,
    config.timezone,
    config.weekendBehavior,
    config.lunchEnabled,
    config.lunchStart,
    config.lunchEnd,
    config.holidayEnabled,
    config.holidays,
  ]);
  const previewStatus = useMemo(
    () => calculateStoreStatus(previewSchedule, config.planTier as any).status,
    [previewSchedule, config.planTier]
  );
  const shouldShowStatusDot = config.isEnabled && config.planTier !== 'free' && (config.showStatusDot ?? true);
  const statusDotColor = getStatusDotColor(previewStatus);
  const statusDotLabel = getStatusDotLabel(previewStatus, config);

  const paddingW = isMobile ? 32 : 28;
  const chatLabel = apps.chatbot?.label || 'Ask Assistant...';
  const chatZoneW = showChatZone ? (isMobile ? 44 : Math.max(220, Math.min(420, 84 + chatLabel.length * 8))) : 0;
  const dividerW = (showChatZone && showAppZone) ? (isMobile ? 12 : 18) : 0;
  const shouldShowBranding = config.planTier === 'free';

  // Desktop: icon=42px, gap=6px. Mobile: icon=36px, gap=5px.
  const iconDim = isMobile ? 36 : 42;
  const iconGap = isMobile ? 5 : 6;
  const expandedVisibleCount = isMobile ? enabledApps.length : Math.min(enabledApps.length, maxVis * 2);
  const hasDesktopExpandedOverflow = !isMobile && enabledApps.length > expandedVisibleCount;
  const visibleApps = (() => {
    if (dockState !== 'allApps' || isMobile) return enabledApps.slice(0, maxVis);
    return hasDesktopExpandedOverflow ? enabledApps : enabledApps.slice(0, expandedVisibleCount);
  })();
  const overflowCount = enabledApps.length - visibleApps.length;
  const hasOverflow = enabledApps.length > maxVis;
  const scrollViewportSlots = dockState === 'allApps' && !isMobile ? expandedVisibleCount : maxVis;
  // Scroll area width = exactly one expanded "page" of icons
  const scrollAreaW = scrollViewportSlots * iconDim + Math.max(0, scrollViewportSlots - 1) * iconGap;

  const appZoneW = (() => {
    if (!showAppZone) return 0;
    if (isMobile) {
      const slots = visibleApps.length + (hasOverflow && dockState !== 'allApps' ? 1 : 0);
      return slots * iconDim + Math.max(0, slots - 1) * iconGap;
    }
    if (dockState === 'allApps' && hasDesktopExpandedOverflow) {
      // Scroll mode: [<] gap [scrollable area = expandedVisibleCount icons wide] gap [>] gap [-]
      return 28 + iconGap + scrollAreaW + iconGap + 28 + iconGap + 42;
    }
    if (dockState === 'allApps') {
      return expandedVisibleCount * iconDim + Math.max(0, expandedVisibleCount - 1) * iconGap + iconGap + 42;
    }
    // Initial mode: visible icons + optional +N
    let w = visibleApps.length * iconDim + Math.max(0, visibleApps.length - 1) * iconGap;
    if (hasOverflow) w += iconGap + iconDim;
    return w;
  })();

  const contentWidth = chatZoneW + appZoneW + dividerW + paddingW;
  const mobileDockCloseW = isMobile && isExpanded && config.launchStyle !== 'container' ? 54 : 0;
  const mobileMaxExpandedWidth = Math.max(btnSize, MOBILE_VIEWPORT - PHONE_BORDER * 2 - mobileDockCloseW);
  const expandedWidth = isMobile ? Math.min(mobileMaxExpandedWidth, contentWidth) : Math.min(Math.max(800, (canvasW ?? 0) - 120), contentWidth);
  const mobileDockOuterWidth = expandedWidth + mobileDockCloseW;
  const mobileDockLeft = isMobile && !isLeft && isExpanded
    ? Math.max(0, anchorLayout.left + btnSize - mobileDockOuterWidth)
    : anchorLayout.left;
  const side = isLeft ? 'left' : 'right';

  const flexDir: React.CSSProperties['flexDirection'] = isLeft ? 'row-reverse' : 'row';
  const effectiveDockGlassStyle: GlassStyle = isFree ? 'solid' : (config.dockGlassStyle || 'solid');
  const shouldCenterDockContent = (showAppZone && !showChatZone) || (showChatZone && !showAppZone);
  const appsZoneSpacingStyle = !isMobile && showChatZone
    ? (isLeft ? { marginRight: 6 } : { marginLeft: 6 })
    : undefined;
  const dockChatStyle: SurfaceStyleTokens = effectiveDockGlassStyle === 'tinted'
    ? {
      background: 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.12)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
    }
    : effectiveDockGlassStyle === 'glass'
      ? {
        background: 'rgba(255,255,255,0.18)',
        border: '1px solid rgba(255,255,255,0.22)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.22)',
      }
      : {
        background: glass.chatZoneBg,
        border: glass.chatZoneBorder,
        boxShadow: 'none',
      };
  const dockControlStyle: SurfaceStyleTokens = effectiveDockGlassStyle === 'tinted'
    ? {
      background: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.10)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
    }
    : effectiveDockGlassStyle === 'glass'
      ? {
        background: 'rgba(255,255,255,0.14)',
        border: '1px solid rgba(255,255,255,0.18)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.16)',
      }
      : {
        background: glass.chatZoneBg,
        border: glass.chatZoneBorder,
        boxShadow: 'none',
      };

  // Shared inner zones
  const chatbotZone = showChatZone ? (
    <div className={`dock-zone${activeZone === 'chatbot' ? ' dock-active' : ''}${hoveredZone === 'chatbot' ? ' dock-hover' : ''}`}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: isMobile ? '0px' : '4px 12px 4px 10px', borderRadius: 9999, background: isMobile ? glass.chatZoneBg : dockChatStyle.background, border: isMobile ? glass.chatZoneBorder : dockChatStyle.border, boxShadow: hoveredZone === 'chatbot' ? undefined : (isMobile ? 'none' : dockChatStyle.boxShadow), flex: isMobile ? '0 0 auto' : '0 0 auto', width: isMobile ? 44 : 'max-content', height: isMobile ? 44 : 46, minWidth: isMobile ? 0 : 160, cursor: 'pointer', flexDirection: 'row' }}
      onMouseEnter={() => setHoveredZone('chatbot')}
      onMouseLeave={() => setHoveredZone(isExpanded ? 'dock' : null)}
      onClick={e => { e.stopPropagation(); setDockState('chat'); onZoneClick('chatbot'); }}>
      <div style={{ width: 38, height: 38, borderRadius: '50%', background: config.anchorColor || '#000000', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'white', outline: 'none' }}>
        {config.chatbotEntryAvatar
          ? <img src={config.chatbotEntryAvatar} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'contain', padding: 4, boxSizing: 'border-box', display: 'block' }} />
          : <Bot size={18} />}
      </div>
      {!isMobile && (
        <div style={{ flex: '0 0 auto', minWidth: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: glass.textColor, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }}>
            {chatLabel}
          </span>
          <div style={{ display: 'flex', gap: 3, alignItems: 'center', opacity: 0.6 }}>
            <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut', delay: 0 }} style={{ width: 5, height: 5, borderRadius: '50%', background: glass.textColor }} />
            <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut', delay: 0.2 }} style={{ width: 5, height: 5, borderRadius: '50%', background: glass.textColor }} />
            <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }} style={{ width: 5, height: 5, borderRadius: '50%', background: glass.textColor }} />
          </div>
        </div>
      )}
    </div>
  ) : null;

  const dividerEl = (showChatZone && showAppZone)
    ? <div style={{ width: 1, height: 24, background: glass.dividerColor, flexShrink: 0, margin: '0 4px' }} />
    : null;

  const appsZone = showAppZone ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: iconGap, flexShrink: 0, ...appsZoneSpacingStyle }}>
      {/* Scroll left (desktop allApps only) */}
      {!isMobile && dockState === 'allApps' && hasDesktopExpandedOverflow && (
        <div onClick={scrollLeftBtn} style={{ width: 28, height: 28, borderRadius: '50%', background: dockControlStyle.background, border: dockControlStyle.border, boxShadow: dockControlStyle.boxShadow, color: glass.textColor, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
          <ChevronLeft size={16} strokeWidth={2.5} />
        </div>
      )}

      <div
        ref={scrollRef}
        onWheel={handleWheel}
        style={{
          display: 'flex', alignItems: 'center', gap: iconGap,
          padding: isMobile ? '3px 2px' : '3px 0', flexShrink: 0,
          overflowX: (!isMobile && dockState === 'allApps' && hasDesktopExpandedOverflow) ? 'auto' : 'visible',
          maxWidth: (!isMobile && dockState === 'allApps' && hasDesktopExpandedOverflow) ? `${scrollAreaW}px` : 'none',
          scrollbarWidth: 'none',
        }}>
        {visibleApps.map(([rawId, app]: [string, any]) => {
          const id = rawId.toLowerCase();
          const meta = CHANNEL_META[id];
          if (!meta) return null;
          const Icon = meta.icon;
          const bgColor = app.color || meta.color || '#333';
          const iconColor = getBrandIconColor(bgColor);

          return (
            <div key={id} className={`dock-zone${activeZone === 'app_item' && selectedChannelId === id ? ' dock-active' : ''}${hoveredZone === 'app_item' && hoveredApp === id ? ' dock-hover' : ''}`}
              ref={el => { if (el) iconRefs.current.set(id, el); else iconRefs.current.delete(id); }}
              style={{
                width: iconDim, height: iconDim, borderRadius: '50%',
                background: bgColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', flexShrink: 0,
                boxShadow: (hoveredZone === 'app_item' && hoveredApp === id) ? undefined : `0 2px 8px ${bgColor}55`,
                border: bgColor.toLowerCase() === '#ffffff' ? '1px solid #e2e8f0' : 'none'
              }}
              onClick={e => { e.stopPropagation(); setSelectedChannelId(id); onZoneClick('app_item'); }}
              onMouseEnter={() => { if (!isMobile) { setHoveredApp(id); setHoveredZone('app_item'); } }}
              onMouseLeave={() => { setHoveredApp(null); setHoveredZone(isExpanded ? 'dock' : null); }}>
              {Icon && <Icon size={isMobile ? 17 : 20} color={iconColor} />}
            </div>
          );
        })}
        {/* +N pill (initial mode only, same size as icon) */}
        {hasOverflow && dockState !== 'allApps' && (
          <div
            className={hoveredZone === 'dock' ? 'dock-zone dock-hover' : 'dock-zone'}
            onClick={(e) => { e.stopPropagation(); setDockState('allApps'); onZoneClick('dock'); }}
            onMouseEnter={() => setHoveredZone('dock')}
            onMouseLeave={() => setHoveredZone(isExpanded ? 'dock' : null)}
            style={{ width: iconDim, height: iconDim, borderRadius: '50%', background: glass.chatZoneBg, border: glass.chatZoneBorder, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}>
            <span style={{ color: glass.textColor, fontSize: 11, fontWeight: 700 }}>+{overflowCount}</span>
          </div>
        )}
      </div>

      {/* Scroll right (desktop allApps only) */}
      {!isMobile && dockState === 'allApps' && hasDesktopExpandedOverflow && (
        <div onClick={scrollRightBtn} style={{ width: 28, height: 28, borderRadius: '50%', background: dockControlStyle.background, border: dockControlStyle.border, boxShadow: dockControlStyle.boxShadow, color: glass.textColor, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
          <ChevronRight size={16} strokeWidth={2.5} />
        </div>
      )}

      {/* Minimize button (desktop allApps only) */}
      {!isMobile && dockState === 'allApps' && (
        <div
          onClick={(e) => { e.stopPropagation(); setDockState('expanded'); onZoneClick('dock'); }}
          style={{ width: 42, height: 42, borderRadius: '50%', background: dockControlStyle.background, border: dockControlStyle.border, boxShadow: dockControlStyle.boxShadow, color: glass.textColor, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
          <Minus size={20} />
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="dock-editor-preview" style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <style>{`
        @keyframes reach-pulse {
          0% { box-shadow: 0 0 0 0 var(--reach-pulse-color, rgba(49, 46, 129, 0.4)); }
          70% { box-shadow: 0 0 0 15px transparent; }
          100% { box-shadow: 0 0 0 0 transparent; }
        }
        .reach-pulse { animation: reach-pulse 2s infinite; }
        @keyframes reach-breath {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.06); }
        }
        .reach-breath { animation: reach-breath 3s ease-in-out infinite; }
        @keyframes reach-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .reach-float { animation: reach-float 4s ease-in-out infinite; }
        @keyframes reach-glow {
          0%, 100% { box-shadow: 0 10px 30px rgba(0,0,0,0.25); filter: brightness(1); }
          50% { box-shadow: 0 10px 45px var(--reach-pulse-color, rgba(49, 46, 129, 0.6)); filter: brightness(1.2); }
        }
        .reach-glow { animation: reach-glow 3s ease-in-out infinite; }
        @keyframes reach-rotate-gradient {
          from { filter: hue-rotate(0deg); }
          to { filter: hue-rotate(360deg); }
        }
        .reach-rotate-gradient { animation: reach-rotate-gradient 8s linear infinite; }
        @keyframes reach-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .reach-spin { animation: reach-spin 8s linear infinite; }
      `}</style>

      <motion.div
        style={{
          position: 'absolute',
          display: 'flex', alignItems: 'center', gap: 10, flexDirection: flexDir, zIndex: 999,
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'none',
          ...(isLeft
            ? { left: anchorLayout.left }
            : isMobile
              ? { left: mobileDockLeft }
              : { right: (canvasW ?? 0) - (anchorLayout.left + btnSize) }),
          top: anchorLayout.top,
          // Hide button when chat or allApps open on mobile
          ...(isMobile && (dockState === 'chat' || dockState === 'allApps') ? { display: 'none' } : {})
        }}
        onPointerDown={onPointerDown}
      >
        {/* Move Hint / Drag Prompt - Contextual feedback */}
        {!isExpanded && activeZone === 'button' && isDragMode && (
          <motion.div
            key={(effectivePosition ?? config.widgetPosition) === 'left' ? 'bubble-right' : 'bubble-left'}
            initial={(effectivePosition ?? config.widgetPosition) === 'left' ? { opacity: 0, x: -10 } : { opacity: 0, x: 10 }}
            animate={(effectivePosition ?? config.widgetPosition) === 'left' ? { opacity: 1, x: 8 } : { opacity: 1, x: -8 }}
            style={(() => {
              const bubbleIsLeft = (effectivePosition ?? config.widgetPosition) === 'left';
              const sharedStyle: React.CSSProperties = {
                position: 'absolute',
                top: '0%',
                transform: 'translateY(-50%)',
                background: isDragMode ? '#0f172a' : '#D81B60',
                color: 'white',
                padding: isDragMode ? '6px 12px' : '4px 10px',
                borderRadius: isDragMode ? 12 : 8,
                fontSize: isDragMode ? 12 : 10,
                fontWeight: 800,
                textTransform: isDragMode ? 'none' : 'uppercase',
                letterSpacing: isDragMode ? 'normal' : '0.05em',
                boxShadow: isDragMode ? '0 10px 25px -5px rgba(0,0,0,0.3)' : '0 4px 12px rgba(216, 27, 96, 0.4)',
                whiteSpace: 'nowrap',
                zIndex: 100,
                pointerEvents: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                border: isDragMode ? '1px solid rgba(255,255,255,0.1)' : 'none',
                backdropFilter: isDragMode ? 'blur(12px)' : 'none',
              };

              if (bubbleIsLeft) {
                // Button is on the left — show bubble to its right
                return { ...sharedStyle, left: '100%', marginLeft: 10 };
              } else {
                // Button is on the right — show bubble to its left
                return { ...sharedStyle, right: '100%', marginRight: 10 };
              }
            })()}
            onPointerDown={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex-container', justifyContent: 'center', alignItems: 'center' }}>
              <p style={{ padding: '2px', marginBottom: 4, color: '#ffffff' }}>Press and hold the button to drag</p>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}>
                <button
                  onPointerDown={e => e.stopPropagation()}
                  onClick={e => { e.stopPropagation(); onCancel?.(); }}
                  style={{
                    padding: '4px 10px',
                    borderRadius: 6,
                    background: 'rgba(255,255,255,0.1)',
                    color: 'white',
                    border: 'none',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onPointerDown={e => e.stopPropagation()}
                  onClick={e => { e.stopPropagation(); onCommit?.(); }}
                  style={{
                    padding: '4px 12px',
                    borderRadius: 6,
                    background: 'var(--brand-secondary)',
                    color: 'white',
                    border: 'none',
                    fontSize: 11,
                    fontWeight: 800,
                    cursor: 'pointer'
                  }}
                >
                  Apply
                </button>
              </div>

            </div>
          </motion.div>
        )}
        {/* Back-in-X bubble — supersedes chatbot tip when offlineMode is back_in_x */}
        {!isExpanded && !isChatOpen && config.offlineMode === 'back_in_x' && (previewStatus !== 'open' || isEvaluatingOfflineStrategy) && (
          <div
            className={`dock-zone${hoveredZone === 'button' ? ' dock-hover' : ''}`}
            onMouseEnter={() => setHoveredZone('button')}
            onMouseLeave={() => setHoveredZone(null)}
            onClick={e => { e.stopPropagation(); onZoneClick('button'); }}
            style={{
              position: 'absolute', bottom: btnSize + 12, [side]: 0,
              backgroundColor: config.backInBgColor || '#09090b',
              color: config.backInTextColor || '#f4f4f5',
              fontFamily: config.backInFontFamily && config.backInFontFamily !== 'inherit' ? `"${config.backInFontFamily}", sans-serif` : undefined,
              fontSize: config.backInFontSize || 13,
              padding: '8px 14px', borderRadius: 14, whiteSpace: 'nowrap',
              display: 'flex', flexDirection: 'column', alignItems: isLeft ? 'flex-end' : 'flex-start',
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.06)',
              cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: '0.75em', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: config.backInLabelColor || '#a5b4fc', marginBottom: 2 }}>
              {config.backInLabel || 'Back in'}
            </span>
            <span style={{ fontWeight: config.backInFontWeight || 700, fontSize: '1.1em' }}>2h 30m</span>
            <div style={{ position: 'absolute', top: '100%', [side]: 18, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `5px solid ${config.backInBgColor || '#09090b'}` }} />
          </div>
        )}

        {/* Chatbot tip bubble — Growth+ only, hidden when back_in_x supersedes it */}
        {!isExpanded && !isChatOpen && config.chatbotTipEnabled && config.offlineMode !== 'back_in_x' && config.planTier !== 'free' && (
          <div style={{
            position: 'absolute', bottom: btnSize + 10, [side]: 0,
            background: config.chatbotTipBgColor || '#D81B60', color: config.chatbotTipTextColor || '#fff',
            fontSize: config.chatbotTipFontSize || 12, fontWeight: config.chatbotTipFontWeight || 600,
            fontFamily: config.chatbotTipFontFamily && config.chatbotTipFontFamily !== 'inherit' ? config.chatbotTipFontFamily : undefined,
            padding: '6px 12px', borderRadius: 10, whiteSpace: 'nowrap',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)', pointerEvents: 'none',
          }}>
            {config.chatbotTipText || 'Need help?'}
            <div style={{ position: 'absolute', bottom: -5, [side]: 14, width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `5px solid ${config.chatbotTipBgColor || '#D81B60'}` }} />
          </div>
        )}


        {/* Branding caption — floats just above the anchor, only while dock is open */}
        <AnimatePresence>
          {shouldShowBranding && isExpanded && (
            <motion.a
              href="https://veahub.com"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.18 }}
              style={{
                position: 'absolute',
                bottom: btnSize + (isMobile ? 16 : 8),
                [side]: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                textDecoration: 'none',
                background: 'rgba(255,255,255,0.82)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(0,0,0,0.07)',
                borderRadius: 20,
                padding: '2px 8px 2px 6px',
                zIndex: 100,
              }}
            >
              <Sparkles size={7} style={{ color: '#6366f1', fill: '#6366f1', flexShrink: 0 }} />
              <span style={{ fontSize: 8.5, fontWeight: 700, color: 'rgba(0,0,0,0.38)', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: '"Plus Jakarta Sans", sans-serif', whiteSpace: 'nowrap' }}>
                Powered by VeaHub
              </span>
            </motion.a>
          )}
        </AnimatePresence>

        {/* Status dot */}
        {!isExpanded && !isChatOpen && shouldShowStatusDot && (() => {
          const dotSize = Math.max(11, Math.round(btnSize * 0.19));
          const dotInset = Math.max(2, Math.round(btnSize * 0.06));
          return (
            <>
              {!isMobile && (config.statusDotTooltipEnabled ?? true) && hoveredStatusDot && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    ...(isLeft ? { left: '100%', marginLeft: 8 } : { right: '100%', marginRight: 8 }),
                    background: '#0f172a',
                    color: '#f8fafc',
                    padding: '6px 10px',
                    borderRadius: 9999,
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.22)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    zIndex: 30,
                    pointerEvents: 'none',
                  }}
                >
                  {statusDotLabel}
                </div>
              )}
              <div
                role="img"
                aria-label={`${statusDotLabel} status`}
                onMouseEnter={() => { if (!isMobile) setHoveredStatusDot(true); }}
                onMouseLeave={() => setHoveredStatusDot(false)}
                style={{
                  position: 'absolute',
                  top: dotInset,
                  right: dotInset,
                  width: dotSize,
                  height: dotSize,
                  borderRadius: '50%',
                  background: statusDotColor,
                  border: '2px solid white',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                  zIndex: 120,
                }}
              />
            </>
          );
        })()}

        {/* Main pill */}
        {(() => {
          const isContainer = config.launchStyle === 'container';
          const isChatFirst = config.launchStyle === 'chat_first';
          // In container mode the pill never expands — it stays as a round button
          const pillExpanded = isExpanded && !isContainer && !isChatFirst;
          const idleAnimation = config.planTier === 'free' ? 'none' : (config.launcherAnimation || 'none');
          const idleAnimationClass = !pillExpanded ? (() => {
            if (idleAnimation === 'none') return '';
            if (idleAnimation === 'rotate' || idleAnimation === 'spin') return 'reach-spin';
            if (idleAnimation === 'rotate_gradient') return 'reach-rotate-gradient';
            return `reach-${idleAnimation}`;
          })() : '';
          return (
            <motion.div
              key={`anchor-${idleAnimation}-${pillExpanded ? 'expanded' : 'closed'}`}
              initial={false}
              whileHover={(() => {
                if (pillExpanded) return {};
                const effect = config.planTier === 'free' ? 'none' : (config.launcherHoverEffect || 'rotate');
                if (effect === 'expand') return { scale: 1.12 };
                if (effect === 'lift') return { scale: 1.08, y: -5 };
                if (effect === 'tilt') return { rotateY: 20 };
                if (effect === 'none') return {};
                return { scale: 1.1, rotate: -5 };
              })()}
              whileTap={pillExpanded ? {} : { scale: 0.95 }}
              animate={{
                backgroundColor: pillExpanded ? glass.pillBg : activeColor,
                borderRadius: pillExpanded ? 9999 : btnRadius,
                width: pillExpanded ? expandedWidth : btnSize,
                height: pillExpanded ? 56 : btnSize,
                padding: pillExpanded ? (isMobile ? '10px 16px' : '10px 14px') : '0px',
                backdropFilter: pillExpanded ? glass.backdropFilter : 'blur(0px) saturate(100%)',
                WebkitBackdropFilter: pillExpanded ? glass.backdropFilter : 'blur(0px) saturate(100%)',
                boxShadow: pillExpanded ? glass.shadow : '0 4px 16px rgba(0,0,0,0.2)',
                borderColor: pillExpanded ? glass.border : 'rgba(0,0,0,0)',
              }}
              style={{
                position: 'relative', display: 'flex', alignItems: 'center',
                justifyContent: pillExpanded && shouldCenterDockContent ? 'center' : (pillExpanded ? 'flex-start' : 'center'),
                borderWidth: '1px', borderStyle: 'solid',
                cursor: pillExpanded ? 'default' : 'pointer',
                overflow: 'hidden',
                '--reach-pulse-color': `${activeColor}66`,
              } as any}
              transition={{ type: 'tween', ease: [0.25, 0.1, 0.25, 1], duration: 0.22 }}
              className={`dock-zone${pillExpanded ? (activeZone === 'dock' ? ' dock-active' : '') : (activeZone === 'button' ? ' dock-launcher-active' : '')}${hoveredZone === (pillExpanded ? 'dock' : 'button') ? ' dock-hover' : ''} ${idleAnimationClass}`}
              onMouseEnter={() => setHoveredZone(pillExpanded ? 'dock' : 'button')}
              onMouseLeave={() => setHoveredZone(null)}
              onClick={pillExpanded ? e => {
                if (isDragMode) return;
                e.stopPropagation();
                onZoneClick('dock');
              } : undefined}
            >
              <AnimatePresence initial={false}>
                {/* Button icon — only when not expanded in dock mode */}
                {!pillExpanded && (
                  <motion.div
                    key="anchor"
                    initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.7 }}
                    transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
                    style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', cursor: 'pointer' }}
                    onClick={e => {
                      if (isDragMode) return;
                      e.stopPropagation();
                      if (isChatFirst) {
                        if (isChatOpen) {
                          setShowTapHint(false);
                          setDockState('closed');
                          onZoneClick('button');
                        } else if (showTapHint) {
                          setShowTapHint(false);
                          setDockState('chat');
                          onZoneClick('chatbot');
                        } else {
                          setShowTapHint(true);
                          onZoneClick('button');
                        }
                        return;
                      }
                      if (isChatOpen) {
                        setDockState(isContainer ? 'allApps' : 'expanded');
                        onZoneClick('dock');
                        return;
                      }
                      if (isContainer && isExpanded) {
                        setShowTapHint(false);
                        setDockState('closed');
                        onZoneClick(null);
                        return;
                      }
                      if (showTapHint) {
                        setShowTapHint(false);
                        setDockState(isContainer && isMobile ? 'allApps' : 'expanded');
                        onZoneClick('dock');
                      } else {
                        setShowTapHint(true);
                        onZoneClick('button');
                      }
                    }}
                  >
                    {isChatOpen ? (
                      isChatFirst ? <X size={iconSize} /> : <ChevronLeft size={iconSize} />
                    ) : (isContainer && isExpanded) ? (
                      <X size={iconSize} />
                    ) : config.anchorAvatar ? (
                      <img src={config.anchorAvatar} alt="Avatar" style={{ position: 'absolute', inset: -6, width: 'calc(100% + 12px)', height: 'calc(100% + 12px)', objectFit: 'cover', borderRadius: btnRadius }} />
                    ) : (
                      config.buttonIcon === 'round' ? <MessageCircle size={iconSize} /> : <MessageSquare size={iconSize} />
                    )}
                  </motion.div>
                )}

                {/* Expanded dock arms — dock mode only */}
                {pillExpanded && (
                  <motion.div
                    key="dock"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, position: 'absolute' }}
                    transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
                    style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: shouldCenterDockContent ? 'center' : 'flex-start' }}
                  >
                    {isLeft ? (
                      <>{appsZone}{dividerEl}{chatbotZone}</>
                    ) : (
                      <>{chatbotZone}{dividerEl}{appsZone}</>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })()}

        {/* X close button — dock mode only */}
        <AnimatePresence>
          {isExpanded && config.launchStyle !== 'container' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              className={`dock-zone${hoveredZone === 'dock' ? ' dock-hover' : ''}`}
              style={{
                width: 44, height: 44, borderRadius: btnRadius,
                backgroundColor: glass.xBg,
                border: glass.xBorder,
                boxShadow: glass.xShadow,
                color: glass.xColor,
                backdropFilter: glass.xBackdropFilter,
                WebkitBackdropFilter: glass.xBackdropFilter,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', flexShrink: 0
              }}
              onMouseEnter={() => setHoveredZone('dock')}
              onMouseLeave={() => setHoveredZone(null)}
              onClick={e => {
                e.stopPropagation();
                setDockState('closed');
                onZoneClick(null);
              }}>
              <X size={14} strokeWidth={2.5} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Container popup — desktop, container mode only ── */}
        {(() => {
          if (config.launchStyle !== 'container' || isMobile || !isExpanded || isChatOpen) return null;

          const cStyle = config.containerGlassStyle || 'glass';
          const cTint = config.containerTintColor || config.anchorColor || '#1a1a2e';
          const cGlass = buildGlassTokens(cStyle, config.containerOpacity ?? 90, cTint);
          const showLabels = config.dockContainerLabels !== false;
          const chatEnabled = config.apps?.chatbot?.enabled !== false && showChatZone;
          const chatLabel = (config.apps as any)?.chatbot?.label || 'Ask Assistant...';
          const anchorBg = config.anchorColor || '#000000';
          const anchorText = getContrastColor(anchorBg);
          const cols = config.containerColumns ?? 3;
          const iconPx = cols <= 2 ? 60 : cols === 4 ? 44 : 52;
          const popupBaseW = cols === 1 ? 220 : cols === 2 ? 240 : cols === 4 ? 320 : 272;
          const popupW = Math.max(popupBaseW, Math.min(420, 84 + chatLabel.length * 8));
          const showContainerTitle = config.dockContainerTitleEnabled === true;
          const containerTitle = (config.dockContainerTitle || '').trim() || 'Get in Touch';
          const containerTitleAlign = config.dockContainerTitleAlign || 'center';
          const containerTrayStyle: SurfaceStyleTokens & { borderRadius: number; padding: number } = cStyle === 'tinted'
            ? {
              background: 'rgba(255,255,255,0.06)',
              borderRadius: 14,
              padding: 10,
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.12)',
            }
            : cStyle === 'glass'
              ? {
                background: 'rgba(255,255,255,0.22)',
                borderRadius: 14,
                padding: 10,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.24)',
                border: '1px solid rgba(255,255,255,0.22)',
              }
              : {
                background: 'rgba(255,255,255,0.14)',
                borderRadius: 14,
                padding: 10,
                boxShadow: 'inset 0 1px 4px rgba(255,255,255,0.14), inset 0 -1px 6px rgba(15,23,42,0.05)',
                border: '1px solid rgba(255,255,255,0.18)',
              };
          const containerChatStyle: SurfaceStyleTokens = cStyle === 'tinted'
            ? {
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
            }
            : cStyle === 'glass'
              ? {
                background: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.22)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.22)',
              }
              : {
                background: cGlass.chatZoneBg,
                border: cGlass.chatZoneBorder || `1px solid ${cGlass.border}`,
                boxShadow: 'none',
              };

          return (
            <AnimatePresence>
              <motion.div
                className={`dock-zone${activeZone === 'dock' ? ' dock-active' : ''}${hoveredZone === 'dock' ? ' dock-hover' : ''}`}
                key="container-popup"
                initial={{ opacity: 0, y: 14, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 14, scale: 0.94 }}
                transition={{ type: 'spring', damping: 28, stiffness: 300 }}
                onMouseEnter={() => setHoveredZone('dock')}
                onMouseLeave={() => setHoveredZone(null)}
                onClick={e => { e.stopPropagation(); onZoneClick('dock'); }}
                style={{
                  position: 'absolute',
                  [isLeft ? 'left' : 'right']: 0,
                  bottom: btnSize + 14,
                  width: popupW,
                  background: cGlass.pillBg,
                  backdropFilter: cGlass.backdropFilter,
                  WebkitBackdropFilter: cGlass.backdropFilter,
                  borderRadius: 20,
                  border: `1px solid ${cGlass.border}`,
                  boxShadow: cGlass.shadow,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0,
                  zIndex: 998,
                  overflow: 'hidden',
                }}
              >
                {showContainerTitle && (
                  <div style={{ padding: '14px 14px 0', display: 'flex', justifyContent: containerTitleAlign === 'left' ? 'flex-start' : containerTitleAlign === 'right' ? 'flex-end' : 'center' }}>
                    <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.01em', color: cGlass.textColor, opacity: 0.72, textAlign: containerTitleAlign }}>
                      {containerTitle}
                    </div>
                  </div>
                )}
                {/* Recessed app grid */}
                <div style={{ padding: showContainerTitle ? '10px 12px 12px' : 12, paddingBottom: chatEnabled ? 8 : 12 }}>
                  <div style={containerTrayStyle}>
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 8 }}>
                      {enabledApps.map(([rawId, app]: [string, any]) => {
                        const id = rawId.toLowerCase();
                        const meta = CHANNEL_META[id];
                        if (!meta) return null;
                        const Icon = meta.icon;
                        const bgColor = app.color || meta.color || '#333';
                        const iconColor = getBrandIconColor(bgColor);
                        return (
                          <div key={id}
                            className={`dock-zone${activeZone === 'app_item' && selectedChannelId === id ? ' dock-active' : ''}${hoveredZone === 'app_item' && hoveredApp === `container-${id}` ? ' dock-hover' : ''}`}
                            ref={el => { if (el) iconRefs.current.set(`container-${id}`, el); else iconRefs.current.delete(`container-${id}`); }}
                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '10px 4px', borderRadius: 12, cursor: 'pointer', transition: 'background 0.15s' }}
                            onClick={e => { e.stopPropagation(); setSelectedChannelId(id); onZoneClick('app_item'); }}
                            onMouseEnter={() => { setHoveredApp(`container-${id}`); setHoveredZone('app_item'); }}
                            onMouseLeave={() => { setHoveredApp(null); setHoveredZone('dock'); }}
                          >
                            <div style={{ width: iconPx, height: iconPx, borderRadius: btnRadius, background: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 16px ${bgColor}44`, border: bgColor.toLowerCase() === '#ffffff' ? '1px solid #e2e8f0' : 'none', flexShrink: 0 }}>
                              {Icon && <Icon size={cols <= 2 ? 28 : cols === 4 ? 20 : 24} color={iconColor} />}
                            </div>
                            {showLabels && (
                              <span style={{ fontSize: 10, fontWeight: 700, color: cGlass.textColor, textAlign: 'center', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.75 }}>
                                {app.label || meta.label}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Chatbot entry — full-width pill matching dock style */}
                {chatEnabled && (
                  <div style={{ padding: '0 10px 10px' }}>
                    <div
                      className={`dock-zone${activeZone === 'chatbot' ? ' dock-active' : ''}${hoveredZone === 'chatbot' ? ' dock-hover' : ''}`}
                      style={{
                        display: 'flex', alignItems: 'center',
                        background: containerChatStyle.background,
                        border: containerChatStyle.border,
                        boxShadow: hoveredZone === 'chatbot' ? undefined : containerChatStyle.boxShadow,
                        borderRadius: 9999,
                        padding: '4px 4px 4px 10px',
                        gap: 8, cursor: 'pointer', minHeight: 50,
                        flexDirection: 'row',
                      }}
                      onMouseEnter={() => setHoveredZone('chatbot')}
                      onMouseLeave={() => setHoveredZone('dock')}
                      onClick={e => { e.stopPropagation(); setDockState('chat'); onZoneClick('chatbot'); }}
                    >
                      <div style={{ width: 38, height: 38, borderRadius: '50%', background: anchorBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: `0 2px 8px ${anchorBg}60` }}>
                        {config.chatbotEntryAvatar
                          ? <img src={config.chatbotEntryAvatar} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'contain', padding: 4, boxSizing: 'border-box', display: 'block' }} />
                          : <Bot size={18} color={anchorText} />}
                      </div>
                      {/* Text + dots in shared wrapper so dots sit right after text */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flex: 1, minWidth: 0 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 600, color: cGlass.textColor, flexShrink: 1, minWidth: 0 }}>
                          {chatLabel}
                        </span>
                        <div style={{ display: 'flex', gap: 3, alignItems: 'center', flexShrink: 0, opacity: 0.55 }}>
                          <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut', delay: 0 }} style={{ width: 5, height: 5, borderRadius: '50%', background: cGlass.textColor }} />
                          <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut', delay: 0.2 }} style={{ width: 5, height: 5, borderRadius: '50%', background: cGlass.textColor }} />
                          <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }} style={{ width: 5, height: 5, borderRadius: '50%', background: cGlass.textColor }} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Free-tier branding */}
                {shouldShowBranding && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '0 0 8px', opacity: 0.35 }}>
                    <span style={{ fontSize: 8.5, fontWeight: 700, color: cGlass.textColor, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Powered by Reach</span>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          );
        })()}
      </motion.div>

      {/* ── Channel icon tooltip — uses fixed positioning to escape overflow:hidden ── */}
      {hoveredApp && (() => {
        const isContainerIcon = hoveredApp.startsWith('container-');
        const isMobileContainerIcon = hoveredApp.startsWith('mobile-container-');
        const tooltipsEnabled = isMobileContainerIcon
          ? config.mobileContainerTooltips !== false
          : isContainerIcon
            ? config.dockContainerTooltips !== false
            : config.dockTooltipsEnabled !== false;
        if (!tooltipsEnabled) return null;
        if (isMobile && !isMobileContainerIcon) return null;
        if (!isMobile && isMobileContainerIcon) return null;
        const el = iconRefs.current.get(hoveredApp);
        if (!el) return null;
        const rect = el.getBoundingClientRect();
        const rawId = isMobileContainerIcon
          ? hoveredApp.slice('mobile-container-'.length)
          : isContainerIcon
            ? hoveredApp.slice('container-'.length)
            : hoveredApp;
        const label = (config.apps as any)?.[rawId]?.label || CHANNEL_META[rawId]?.label || rawId;
        return (
          <div style={{
            position: 'fixed',
            left: rect.left + rect.width / 2,
            top: rect.top - 10,
            transform: 'translateX(-50%) translateY(-100%)',
            zIndex: 9999,
            background: 'rgba(12,12,14,0.9)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            color: '#ffffff',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.01em',
            padding: '4px 10px 5px',
            borderRadius: 8,
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            pointerEvents: 'none',
          }}>
            {label}
            <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: '4px solid rgba(12,12,14,0.9)' }} />
          </div>
        );
      })()}
    </div >
  );
};


// ─── Main Component ───────────────────────────────────────────────────────────

// System fonts that are available without a Google Fonts request
const DASHBOARD_SYSTEM_FONTS = new Set(['inherit', 'Georgia', 'Arial', 'Helvetica', 'Verdana', 'Calibri', 'Courier New', 'Times New Roman', 'Inter', 'Plus Jakarta Sans']);

export function DockAppearanceEditor({ config, onChange, onSave, shopDomain, isGrowthPlus, isAgentPlus, isDirty, setLocalHolidays, setShowHolidayModal }: DockAppearanceEditorProps) {
  const [activeZone, setActiveZone] = useState<ZoneId>(null);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [dockState, setDockState] = useState<DockState>('closed');
  const [deviceMode, setDeviceMode] = useState<DeviceMode>('desktop');
  const [draftOfflineMode, setDraftOfflineMode] = useState(config.offlineMode || 'follow_apps');
  const [isEvaluatingOfflineStrategy, setIsEvaluatingOfflineStrategy] = useState(false);

  // Inject Google Fonts into the dashboard page for the live preview
  useEffect(() => {
    const fontFields = [
      config.globalFontFamily,
      config.backInFontFamily,
      config.chatbotTipFontFamily,
      config.chatbotHeaderFontFamily,
      config.chatbotBodyFontFamily,
      config.chatbotSuggestionFontFamily,
    ];
    const needed = [...new Set(
      fontFields.filter((f): f is string => !!f && !DASHBOARD_SYSTEM_FONTS.has(f))
    )];
    if (needed.length === 0) return;

    const alreadyLoaded = (f: string) =>
      !!document.querySelector(`link[href*="${f.replace(/ /g, '+')}"]`);

    const toLoad = needed.filter(f => !alreadyLoaded(f));
    if (toLoad.length === 0) return;

    const params = toLoad.map(f => `family=${f.replace(/ /g, '+')}:wght@400;500;600;700;800;900`).join('&');
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?${params}&display=swap`;
    document.head.appendChild(link);
  }, [config.globalFontFamily, config.backInFontFamily, config.chatbotTipFontFamily, config.chatbotHeaderFontFamily, config.chatbotBodyFontFamily, config.chatbotSuggestionFontFamily]);

  useEffect(() => {
    if (!isEvaluatingOfflineStrategy) {
      setDraftOfflineMode(config.offlineMode || 'follow_apps');
    }
  }, [config.offlineMode, isEvaluatingOfflineStrategy]);

  useEffect(() => {
    if (activeZone !== 'button' && isEvaluatingOfflineStrategy) {
      setIsEvaluatingOfflineStrategy(false);
      setDraftOfflineMode(config.offlineMode || 'follow_apps');
    }
  }, [activeZone, isEvaluatingOfflineStrategy, config.offlineMode]);

  const isChatOpen = dockState === 'chat';
  const effectiveLaunchStyle = resolveEditorLaunchStyle(config.launchStyle, isGrowthPlus, isAgentPlus);
  const previewConfig = isEvaluatingOfflineStrategy
    ? { ...config, offlineMode: draftOfflineMode, launchStyle: effectiveLaunchStyle }
    : { ...config, launchStyle: effectiveLaunchStyle };
  const editorPreviewStatus = useMemo(
    () => calculateStoreStatus({
      isEnabled: previewConfig.isEnabled ?? true,
      startHour: previewConfig.startHour ?? 9,
      endHour: previewConfig.endHour ?? 17,
      timezone: previewConfig.timezone || 'America/New_York',
      weekendBehavior: previewConfig.weekendBehavior ?? 'sunday',
      lunchEnabled: previewConfig.lunchEnabled ?? false,
      lunchStart: previewConfig.lunchStart ?? 12,
      lunchEnd: previewConfig.lunchEnd ?? 13,
      holidayEnabled: previewConfig.holidayEnabled ?? false,
      holidays: previewConfig.holidays ?? [],
    } as ScheduleConfig, previewConfig.planTier as any).status,
    [previewConfig]
  );
  const mobileDockTintColor = config.dockTintColor || config.anchorColor || '#000000';
  const mobileDockGlass = buildGlassTokens(config.planTier === 'free' ? 'solid' : (config.dockGlassStyle || 'solid'), config.planTier === 'free' ? 100 : (config.dockOpacity ?? 100), mobileDockTintColor);
  const mobileContainerTintColor = config.containerTintColor || config.anchorColor || '#1a1a2e';
  const mobileContainerGlass = buildGlassTokens(config.containerGlassStyle || 'glass', config.containerOpacity ?? 90, mobileContainerTintColor);
  const mobileSheetGlass = effectiveLaunchStyle === 'container' ? mobileContainerGlass : mobileDockGlass;

  const renderChatMockup = (isMobile: boolean) => {
    if (!isChatOpen) return null;

    const headerBg = config.botColor || config.anchorColor || '#000000';
    const headerSurface = (config.chatbotHeaderStyle || 'solid') === 'gradient'
      ? `linear-gradient(135deg, ${headerBg} 0%, ${headerBg}e6 28%, ${headerBg}b8 58%, rgba(45, 55, 72, 0.96) 100%)`
      : headerBg;
    const headerTextColor = config.chatbotHeaderTextColor || '#ffffff';
    const chatBg = (() => {
      const hex = (config.chatbotContainerColor || '#fcfdff').replace('#', '');
      const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
      const r = parseInt(full.slice(0, 2), 16), g = parseInt(full.slice(2, 4), 16), b = parseInt(full.slice(4, 6), 16);
      const o = (config.chatbotContainerOpacity ?? 100) / 100;
      return isNaN(r) ? '#fcfdff' : `rgba(${r},${g},${b},${o})`;
    })();

    const chatAnchor = getPreviewAnchorLayout({
      config,
      isMobile,
      canvasWidth,
      canvasHeight,
    });
    const desktopChatWidth = 396;
    const desktopChatHeight = 680;
    const desktopChatGap = Math.max(18, Math.round(chatAnchor.btnSize * 0.3));
    const desktopPreviewScale = Math.min(
      0.78,
      (canvasWidth - 120) / desktopChatWidth,
      (canvasHeight - 120) / desktopChatHeight
    );
    const scaledDesktopChatWidth = desktopChatWidth * desktopPreviewScale;
    const scaledDesktopChatHeight = desktopChatHeight * desktopPreviewScale;

    const headerFontFamily = config.chatbotHeaderFontFamily && config.chatbotHeaderFontFamily !== 'inherit' ? `"${config.chatbotHeaderFontFamily}", sans-serif` : 'inherit';
    const headerFontSize = config.chatbotHeaderFontSize || 14;
    const headerFontWeight = config.chatbotHeaderFontWeight || 700;
    const bodyFontFamily = config.chatbotBodyFontFamily && config.chatbotBodyFontFamily !== 'inherit' ? `"${config.chatbotBodyFontFamily}", sans-serif` : 'inherit';
    const bodyFontSize = config.chatbotBodyFontSize || 14;
    const bodyFontWeight = config.chatbotBodyFontWeight || 400;
    const suggestionFontFamily = config.chatbotSuggestionFontFamily && config.chatbotSuggestionFontFamily !== 'inherit'
      ? `"${config.chatbotSuggestionFontFamily}", sans-serif`
      : bodyFontFamily;
    const suggestionFontSize = config.chatbotSuggestionFontSize || bodyFontSize;
    const suggestionFontWeight = config.chatbotSuggestionFontWeight || 400;
    const previewSupportApps = Object.entries(config.apps || {})
      .filter(([id, app]: [string, any]) => id !== 'chatbot'
        && app.enabled !== false
        && CHANNEL_META[id.toLowerCase()]
        && (editorPreviewStatus === 'open' ? app.showOpen !== false : app.showClosed !== false)
        && (isMobile ? app.showOnMobile !== false : app.showOnDesktop !== false))
      .sort((a, b) => ((a[1] as any).order ?? 0) - ((b[1] as any).order ?? 0));
    const canShowPreviewSupport = effectiveLaunchStyle === 'chat_first' && previewSupportApps.length > 0;
    const closePreviewChat = (e: React.MouseEvent) => {
      e.stopPropagation();
      setPreviewSupportOpen(false);
      if (effectiveLaunchStyle === 'chat_first') {
        setDockState('closed');
        setShowTapHint(false);
        setActiveZone('button');
        return;
      }
      setDockState(effectiveLaunchStyle === 'container' ? 'allApps' : 'expanded');
      setActiveZone('dock');
    };

    return (
      <div
        className={`dock-zone${activeZone === 'chatbot' ? ' dock-active' : ''}`}
        onClick={e => {
          e.stopPropagation();
          handleZoneClick('chatbot');
        }}
        style={{
          ...(isMobile ? {
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            borderRadius: 0,
            zIndex: 2000,
          } : {
            position: 'absolute',
            left: chatAnchor.isLeft
              ? chatAnchor.left
              : chatAnchor.left + chatAnchor.btnSize - scaledDesktopChatWidth,
            top: Math.max(0, chatAnchor.top - scaledDesktopChatHeight - desktopChatGap),
            width: desktopChatWidth,
            height: desktopChatHeight,
            borderRadius: 20,
            zIndex: 100,
            transform: `scale(${desktopPreviewScale})`,
            transformOrigin: 'top left',
          }),
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0,0,0,0.16)',
          display: 'flex',
          flexDirection: 'column',
          background: chatBg,
          cursor: 'default',
        }}>
        <div style={{ height: isMobile ? 62 : 68, background: headerSurface, display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 10, padding: isMobile ? '0 12px' : `0 14px 0 ${((config.chatbotHeaderAvatarEnabled !== false) || isMobile) ? 14 : 22}px`, flexShrink: 0 }}>
          {isMobile && (
            <div
              onClick={closePreviewChat}
              style={{ width: 30, height: 30, borderRadius: 10, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}
            >
              {(previewConfig.offlineMode === 'chatbot' && editorPreviewStatus !== 'open')
                ? <X size={16} color="white" />
                : <ArrowLeft size={16} color="white" />}
            </div>
          )}
          {(config.chatbotHeaderAvatarEnabled !== false) && (
            <div style={{ width: isMobile ? 34 : 38, height: isMobile ? 34 : 38, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {config.chatbotAvatar
                ? <img src={config.chatbotAvatar} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'contain', padding: 4, boxSizing: 'border-box', display: 'block' }} />
                : <Bot size={18} color="white" />}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: headerTextColor, fontSize: isMobile ? Math.min(Math.max(14, headerFontSize), 15) : Math.max(14, headerFontSize), fontWeight: headerFontWeight, fontFamily: headerFontFamily, lineHeight: 1.1, letterSpacing: '-0.015em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{config.botName || 'AI Assistant'}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 6 }}>
              {(config.chatbotHeaderStatusDotEnabled !== false) && (
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', marginTop: 1 }} />
              )}
              <span style={{ color: `${headerTextColor}c7`, fontSize: isMobile ? Math.max(10, headerFontSize - 5) : Math.max(11, headerFontSize - 4), fontFamily: headerFontFamily, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{config.chatbotHeaderTagline || 'Online'}</span>
            </div>
          </div>
          {canShowPreviewSupport && (
            <div
              onClick={e => { e.stopPropagation(); setPreviewSupportOpen((open) => !open); }}
              style={{
                width: isMobile ? 30 : 32,
                height: isMobile ? 30 : 32,
                borderRadius: isMobile ? 10 : 12,
                background: previewSupportOpen ? 'rgba(255,255,255,0.24)' : 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                cursor: 'pointer',
                color: 'white',
                fontSize: 12,
                fontWeight: 700,
              }}
              aria-label="Contact support"
            >
              <LifeBuoy size={isMobile ? 13 : 14} color="white" />
            </div>
          )}
          <div
            onClick={e => {
              e.stopPropagation();
              setPreviewSupportOpen(false);
              handleZoneClick('chatbot');
            }}
            style={{
              width: isMobile ? 30 : 32,
              height: isMobile ? 30 : 32,
              borderRadius: isMobile ? 10 : 12,
              background: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              cursor: 'pointer',
              color: 'white',
            }}
            aria-label="New conversation"
          >
            <SquarePen size={isMobile ? 14 : 15} color="white" />
          </div>
          {!isMobile && (
            <div
              onClick={closePreviewChat}
              style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}
            >
              <X size={16} color="white" />
            </div>
          )}
        </div>
        <div style={{ flex: 1, background: `linear-gradient(180deg, rgba(255,255,255,0.985) 0%, rgba(252,253,255,0.965) 100%)`, padding: 12, overflowY: 'auto', scrollbarColor: headerBg }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#e2e8f0', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Bot size={13} color="#9ca3af" />
            </div>
            <div style={{ background: 'rgba(232, 238, 247, 0.98)', border: '1px solid rgba(148,163,184,0.28)', borderRadius: '14px 14px 14px 3px', padding: '8px 12px', fontSize: bodyFontSize, fontFamily: bodyFontFamily, fontWeight: bodyFontWeight, color: '#1f2937', maxWidth: '80%' }}>
              {config.welcomeMsg || 'Hi! How can I help you?'}
            </div>
          </div>
          {config.initialSuggestions && (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', paddingLeft: 34 }}>
              {config.initialSuggestions
                .split(',')
                .map((s: string) => s.trim())
                .filter(Boolean)
                .filter((s: string) => !(previewConfig.offlineMode === 'chatbot' && s === 'Contact Support'))
                .map((s: string, i: number) => (
                <div key={i} style={{ background: 'transparent', border: `1px solid ${headerBg}`, borderRadius: 999, padding: '6px 12px', fontSize: suggestionFontSize, fontFamily: suggestionFontFamily, fontWeight: suggestionFontWeight, lineHeight: 1.4, color: headerBg }}>{s.trim()}</div>
              ))}
            </div>
          )}
          {previewSupportOpen && canShowPreviewSupport && (
            <div style={{ marginTop: 14, paddingLeft: 34 }}>
              <div style={{ borderRadius: 18, background: 'rgba(232, 238, 247, 0.92)', border: '1px solid rgba(148,163,184,0.24)', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>Contact support</span>
                  <span style={{ fontSize: 11, color: '#64748b', lineHeight: 1.4 }}>These channels appear inside chat in Chat-first mode.</span>
                </div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {previewSupportApps.map(([id, app]: [string, any]) => {
                    const meta = CHANNEL_META[id.toLowerCase()];
                    const Icon = meta?.icon;
                    const bgColor = app.color || meta?.color || '#0f172a';
                    const iconColor = getBrandIconColor(bgColor);
                    return (
                      <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 14, background: '#ffffff', border: '1px solid rgba(226,232,240,0.96)' }}>
                        <div style={{ width: 34, height: 34, borderRadius: 12, background: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: `0 6px 18px ${bgColor}30` }}>
                          {Icon ? <Icon size={16} color={iconColor} /> : null}
                        </div>
                        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{app.label || meta?.label || id}</span>
                          <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{app.value || meta?.fieldLabel || 'Configured contact method'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
        <div style={{ height: 54, background: 'white', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px', flexShrink: 0 }}>
          <div style={{ flex: 1, height: 34, borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', padding: '0 10px', fontSize: bodyFontSize, fontFamily: bodyFontFamily, fontWeight: bodyFontWeight, color: '#9ca3af' }}>
            Type a message...
          </div>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: headerBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Send size={15} color="white" />
          </div>
        </div>
      </div >
    );
  };
  const defaultPreviewUrl = shopDomain && shopDomain !== 'localhost' ? `https://${shopDomain}` : '';
  const [draftUrl, setDraftUrl] = useState(() => readStoredPreviewUrl(defaultPreviewUrl));
  const [proxyHtml, setProxyHtml] = useState<string | null>(() => readStoredPreviewHtml(readStoredPreviewUrl(defaultPreviewUrl)));
  const [isLoadingProxy, setIsLoadingProxy] = useState(false);
  const [isDragMode, setIsDragMode] = useState(false);
  const [showTapHint, setShowTapHint] = useState(false);
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [previewSupportOpen, setPreviewSupportOpen] = useState(false);
  const toggleSaveToast = useCallback(() => setShowSaveToast((active) => !active), []);
  const [pendingConfig, setPendingConfig] = useState<Partial<DockAppearanceConfig> | null>(null);
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [mobileDrawerHoveredApp, setMobileDrawerHoveredApp] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const mobileDrawerIconRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const dragOrigin = useRef({ mouseX: 0, mouseY: 0, btnX: 0, btnY: 0 });
  const tierCapabilityKey = `${isGrowthPlus ? 'growth' : 'base'}-${isAgentPlus ? 'agent' : 'standard'}`;
  const prevTierCapabilityKeyRef = useRef(tierCapabilityKey);
  const isMobile = deviceMode === 'mobile';
  const [mountedPreviewModes, setMountedPreviewModes] = useState<{ desktop: boolean; mobile: boolean }>({ desktop: true, mobile: false });


  const [canvasWidth, setCanvasWidth] = useState(700);
  const [canvasHeight, setCanvasHeight] = useState(700);
  const desktopIframeRef = useRef<HTMLIFrameElement>(null);
  const mobileIframeRef = useRef<HTMLIFrameElement>(null);
  const desktopOverlayRef = useRef<HTMLDivElement>(null);
  const mobileOverlayRef = useRef<HTMLDivElement>(null);
  const resetPreviewToClosed = useCallback(() => {
    setDockState('closed');
    setActiveZone(null);
    setSelectedChannelId(null);
    setShowTapHint(false);
    setPreviewSupportOpen(false);
  }, []);

  useEffect(() => {
    if (prevTierCapabilityKeyRef.current === tierCapabilityKey) return;
    prevTierCapabilityKeyRef.current = tierCapabilityKey;
    resetPreviewToClosed();
  }, [effectiveLaunchStyle, resetPreviewToClosed, tierCapabilityKey]);

  useEffect(() => {
    if (dockState !== 'chat' || effectiveLaunchStyle !== 'chat_first') {
      setPreviewSupportOpen(false);
    }
  }, [dockState, effectiveLaunchStyle]);


  useEffect(() => {
    if (!canvasRef.current) return;
    const obs = new ResizeObserver(entries => {
      setCanvasWidth(entries[0].contentRect.width);
      setCanvasHeight(entries[0].contentRect.height);
    });
    obs.observe(canvasRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    setMountedPreviewModes(prev => (
      deviceMode === 'mobile'
        ? (prev.mobile ? prev : { ...prev, mobile: true })
        : (prev.desktop ? prev : { ...prev, desktop: true })
    ));
  }, [deviceMode]);

  useEffect(() => {
    const bindings = [
      { overlay: mobileOverlayRef.current, iframe: mobileIframeRef.current },
      { overlay: desktopOverlayRef.current, iframe: desktopIframeRef.current },
    ];

    const cleanups = bindings.flatMap(({ overlay, iframe }) => {
      if (!overlay || !iframe?.contentWindow) return [];

      const listener = (event: WheelEvent) => {
        event.preventDefault();
        iframe.contentWindow?.scrollBy({ top: event.deltaY, left: event.deltaX, behavior: 'auto' });
      };

      overlay.addEventListener('wheel', listener, { passive: false });
      return [() => overlay.removeEventListener('wheel', listener)];
    });

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [proxyHtml, mountedPreviewModes.desktop, mountedPreviewModes.mobile]);

  const getBtnPos = useCallback(() => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    const cW = rect.width;
    const cH = rect.height;

    const rawDesktopSize = config.desktopButtonSize || 60;
    const rawMobileSize = config.mobileButtonSize || 52;
    const btnSize = isMobile ? (typeof rawMobileSize === 'number' ? rawMobileSize : 52) : (typeof rawDesktopSize === 'number' ? rawDesktopSize : 60);

    const isLeft = (pendingConfig?.widgetPosition || config.widgetPosition) === 'left';

    const sideMargin = isMobile
      ? (pendingConfig?.mobileSideMargin ?? config.mobileSideMargin ?? 16)
      : (pendingConfig?.desktopSideMargin ?? config.desktopSideMargin ?? 30);

    const bottomMargin = isMobile
      ? (pendingConfig?.mobileBottomMargin ?? config.mobileBottomMargin ?? 20)
      : (pendingConfig?.desktopBottomMargin ?? config.desktopBottomMargin ?? 40);

    let x: number, y: number;
    if (isMobile) {
      // All positions are relative to the inner content div of the phone frame,
      // which is inset by PHONE_BORDER on each side.
      const outerPhoneH = Math.min(780, cH - 32);
      const outerMobileOffset = (cW - MOBILE_VIEWPORT) / 2;
      const innerLeft  = outerMobileOffset + PHONE_BORDER;
      const innerWidth = MOBILE_VIEWPORT - 2 * PHONE_BORDER;
      const innerTop   = (cH - outerPhoneH) / 2 + PHONE_BORDER;
      const innerH     = outerPhoneH - 2 * PHONE_BORDER;
      x = isLeft ? (innerLeft + sideMargin) : (innerLeft + innerWidth - sideMargin - btnSize);
      y = innerTop + innerH - bottomMargin - btnSize;
    } else {
      x = isLeft ? sideMargin : (cW - sideMargin - btnSize);
      y = cH - bottomMargin - btnSize;
    }

    return { x, y };
  }, [config, isMobile, pendingConfig]);

  const dragZone = useMemo(() => {
    if (!isDragging || !canvasRef.current) return null;
    const rect = canvasRef.current.getBoundingClientRect();
    const canvasWidth = rect.width;
    const rawDesktopSize = config.desktopButtonSize || 60;
    const rawMobileSize = config.mobileButtonSize || 52;
    const btnSize = isMobile ? (typeof rawMobileSize === 'number' ? rawMobileSize : 52) : (typeof rawDesktopSize === 'number' ? rawDesktopSize : 60);

    if (isMobile) {
      // dragPos is in inner-div coordinates; compare button centre to inner-div centre
      const innerOriginX = (canvasWidth - MOBILE_VIEWPORT) / 2 + PHONE_BORDER;
      const innerWidth   = MOBILE_VIEWPORT - 2 * PHONE_BORDER;
      return (dragPos.x - innerOriginX + btnSize / 2) < (innerWidth / 2) ? 'left' : 'right';
    }
    return (dragPos.x + btnSize / 2) < (canvasWidth / 2) ? 'left' : 'right';
  }, [isDragging, dragPos, isMobile, config]);

  const handleZoneClick = useCallback((zone: ZoneId) => {
    if (isDragMode) return; // Ignore zone clicks in drag mode
    setActiveZone(zone);
  }, [isDragMode]);

  const startDragMode = useCallback(() => {
    setIsDragMode(true);
    setShowTapHint(true);
    const p = getBtnPos();
    setDragPos(p);
    setPendingConfig({
      widgetPosition: config.widgetPosition,
      desktopSideMargin: config.desktopSideMargin,
      mobileSideMargin: config.mobileSideMargin,
      desktopBottomMargin: config.desktopBottomMargin,
      mobileBottomMargin: config.mobileBottomMargin,
    });
    // Set active zone to button to show the handle clearly
    setActiveZone('button');
  }, [config, getBtnPos]);

  const commitDragMode = useCallback(() => {
    if (pendingConfig) {
      Object.entries(pendingConfig).forEach(([k, v]) => {
        onChange(k, v);
      });
      setShowSaveToast(true);
    }
    setIsDragMode(false);
    // Keep showTapHint true so the "Click/Tap to expand" hint shows after drag ends
    setPendingConfig(null);
  }, [pendingConfig, onChange]);

  const cancelDragMode = useCallback(() => {
    setIsDragMode(false);
    // Keep showTapHint true so the "Click/Tap to expand" hint shows after drag ends
    setPendingConfig(null);
  }, []);

  const handleButtonPointerDown = useCallback((e: React.PointerEvent) => {
    if (!isDragMode) return;
    e.preventDefault(); e.stopPropagation();
    const p = getBtnPos();
    dragOrigin.current = { mouseX: e.clientX, mouseY: e.clientY, btnX: p.x, btnY: p.y };
    setDragPos(p);
    setIsDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [getBtnPos, isDragMode]);

  const handleCanvasPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging || !canvasRef.current || !isDragMode) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const canvasWidth = rect.width;
    const canvasHeight = rect.height;

    const dx = e.clientX - dragOrigin.current.mouseX;
    const dy = e.clientY - dragOrigin.current.mouseY;

    const rawDesktopSize = config.desktopButtonSize || 60;
    const rawMobileSize = config.mobileButtonSize || 52;
    const btnSize = isMobile ? (typeof rawMobileSize === 'number' ? rawMobileSize : 52) : (typeof rawDesktopSize === 'number' ? rawDesktopSize : 60);

    // Determine side based on mouse position relative to canvas center
    const mouseRelX = e.clientX - rect.left;
    const currentSide = mouseRelX < (canvasWidth / 2) ? 'left' : 'right';

    // Compute the coordinate origin — for mobile, use the inner content div of the
    // phone frame (inset by PHONE_BORDER on each side) so dragPos values map 1:1 to
    // the CSS left/right/bottom values written into config.
    let originX: number, originY: number, innerW: number, innerH: number;
    if (isMobile) {
      const outerPhoneH  = Math.min(780, canvasHeight - 32);
      const outerMobOff  = (canvasWidth - MOBILE_VIEWPORT) / 2;
      originX = outerMobOff + PHONE_BORDER;
      originY = (canvasHeight - outerPhoneH) / 2 + PHONE_BORDER;
      innerW  = MOBILE_VIEWPORT - 2 * PHONE_BORDER;
      innerH  = outerPhoneH - 2 * PHONE_BORDER;
    } else {
      originX = 0;
      originY = 0;
      innerW  = canvasWidth;
      innerH  = canvasHeight;
    }

    // Bounds of the 140×140 area on the current side
    const areaSize = 140;
    let minX, maxX;
    if (currentSide === 'left') {
      minX = originX;
      maxX = originX + areaSize - btnSize;
    } else {
      minX = originX + innerW - areaSize;
      maxX = originX + innerW - btnSize;
    }

    const minY = originY + innerH - areaSize;
    const maxY = originY + innerH - btnSize;

    const nx = Math.max(minX, Math.min(maxX, dragOrigin.current.btnX + dx));
    const ny = Math.max(minY, Math.min(maxY, dragOrigin.current.btnY + dy));

    setDragPos({ x: nx, y: ny });

    // Update pending config in real-time for visual feedback
    const relX = nx - originX;
    const relY = ny - originY;
    const newPos = currentSide;
    const newSideMargin = Math.round(newPos === 'right' ? (innerW - relX - btnSize) : relX);
    const newBottomMargin = Math.round(innerH - relY - btnSize);

    setPendingConfig(prev => ({
      ...prev,
      widgetPosition: newPos,
      [isMobile ? 'mobileSideMargin' : 'desktopSideMargin']: Math.max(0, Math.min(100, newSideMargin)),
      [isMobile ? 'mobileBottomMargin' : 'desktopBottomMargin']: Math.max(0, Math.min(100, newBottomMargin)),
    }));
  }, [isDragging, isMobile, config, isDragMode]);

  const handleCanvasPointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDragging || !canvasRef.current) return;
    setIsDragging(false);
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
  }, [isDragging]);

  const openDockConfig = useCallback(() => {
    if (effectiveLaunchStyle === 'chat_first') {
      setDockState('closed');
      setActiveZone('button');
      setSelectedChannelId(null);
      return;
    }
    setDockState(effectiveLaunchStyle === 'container' ? 'allApps' : 'expanded');
    setActiveZone('dock');
    setSelectedChannelId(null);
  }, [effectiveLaunchStyle]);

  const handleBack = useCallback(() => {
    if (activeZone === 'app_item') { setActiveZone('dock'); setSelectedChannelId(null); }
    else if (activeZone === 'chatbot' || activeZone === 'dock' || dockState === 'chat') {
      openDockConfig();
    }
    else { setActiveZone(null); setSelectedChannelId(null); }
  }, [activeZone, dockState, openDockConfig]);

  const loadProxy = useCallback(async (manualUrl?: string) => {
    const urlToLoad = manualUrl ?? draftUrl;

    if (!urlToLoad.trim()) {
      setProxyHtml(null);
      try {
        sessionStorage.removeItem(DOCK_PREVIEW_CACHE_KEY);
        sessionStorage.removeItem(DOCK_PREVIEW_URL_KEY);
      } catch { }
      return;
    }

    setIsLoadingProxy(true);

    try {
      const endpoint = `/api/merchant/proxy-html?url=${encodeURIComponent(urlToLoad)}`;
      const resp = await merchantFetch(endpoint);
      if (resp.ok) {
        const html = sanitizePreviewHtml(await resp.text());
        setProxyHtml(html);
        try {
          sessionStorage.setItem(DOCK_PREVIEW_CACHE_KEY, JSON.stringify({ url: urlToLoad, html }));
          sessionStorage.setItem(DOCK_PREVIEW_URL_KEY, urlToLoad);
        } catch { }
      } else {
        const detail = await resp.text().catch(() => '');
        console.error('Preview load failed', {
          status: resp.status,
          url: urlToLoad,
          detail: detail.slice(0, 200),
        });
        setProxyHtml(null);
      }
    } catch (error) {
      console.error('Preview load threw', { url: urlToLoad, error });
      setProxyHtml(null);
    } finally {
      setIsLoadingProxy(false);
    }
  }, [draftUrl]);

  useEffect(() => {
    if (draftUrl && draftUrl.trim() && !readStoredPreviewHtml(draftUrl)) {
      loadProxy(draftUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only runs once on mount

  useEffect(() => {
    try {
      if (draftUrl.trim()) sessionStorage.setItem(DOCK_PREVIEW_URL_KEY, draftUrl);
      else sessionStorage.removeItem(DOCK_PREVIEW_URL_KEY);
    } catch { }
  }, [draftUrl]);

  const ZONE_TITLES: Record<NonNullable<ZoneId>, string> = {
    'global': 'Global Settings',
    'button': 'Main Button',
    'dock': effectiveLaunchStyle === 'container' ? 'Container' : effectiveLaunchStyle === 'chat_first' ? 'Support Flow' : 'Dock',
    'chatbot': 'Chatbot',
    'app_item': selectedChannelId ? (CHANNEL_META[selectedChannelId.toLowerCase()]?.label ?? 'App Item') : 'App Item',
  };
  const showExpandHint = showTapHint && activeZone === 'button' && dockState === 'closed';
  const showZoneHint = (dockState === 'expanded' || dockState === 'allApps') && !showExpandHint;

  const renderPanel = () => {
    if (!activeZone || activeZone === 'global') return (
      <BlockStack gap="500">
        <BlockStack gap="300">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Text as="p" variant="bodyMd" fontWeight="semibold" tone="subdued">How it works</Text>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b', lineHeight: 1.55 }}>
              Use the preview like a live map of your widget. Click through it step by step, then select the exact part you want to refine and its settings will appear here.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {([
              {
                step: '1',
                title: 'Open the widget',
                text: 'Click the main widget button once in the preview to reveal the widget and start editing from the outside in.',
              },
              {
                step: '2',
                title: 'Expand the view you want',
                text: effectiveLaunchStyle === 'chat_first'
                  ? 'Click the launcher once to target the button, then click again to open chat and use the Support action in the header whenever you want to preview the in-chat contact handoff options.'
                  : `Click again inside the widget to open the ${effectiveLaunchStyle === 'container' ? 'container' : 'dock'} view, jump into the chatbot area, or reach the individual app icons.`,
              },
              {
                step: '3',
                title: 'Select and fine-tune',
                text: 'Hover over different parts of the preview, then click the exact highlighted element you want to change and its settings will load in this panel.',
              },
            ] as const).map(({ step, title, text }) => (
              <div
                key={step}
                style={{
                  display: 'flex',
                  gap: 14,
                  alignItems: 'flex-start',
                  padding: '14px 16px',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.84) 0%, rgba(248,250,252,0.98) 100%)',
                  borderRadius: 12,
                  border: '1px solid var(--dock-panel-step-border)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)',
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'default',
                }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: 'var(--dock-panel-step-dot-bg)',
                    color: 'var(--dock-panel-step-dot-text)',
                    fontSize: 11,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                >
                  {step}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', letterSpacing: '0.01em' }}>
                    {title}
                  </div>
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--dock-panel-step-text)', lineHeight: 1.55 }}>
                    {text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </BlockStack>
      </BlockStack>
    );

    if (activeZone === 'button') return (
      <AnchorPanel
        config={config}
        onChange={onChange}
        isGrowthPlus={isGrowthPlus}
        isAgentPlus={isAgentPlus}
        setLocalHolidays={setLocalHolidays}
        setShowHolidayModal={setShowHolidayModal}
        startDragMode={startDragMode}
        isDragMode={isDragMode}
        onFinishDragMode={commitDragMode}
        draftOfflineMode={draftOfflineMode}
        onDraftOfflineModeChange={setDraftOfflineMode as (value: string) => void}
        onStartOfflineStrategyEvaluation={() => {
          setDraftOfflineMode(config.offlineMode || 'follow_apps');
          setIsEvaluatingOfflineStrategy(true);
        }}
        onCommitOfflineStrategyEvaluation={() => {
          onChange('offlineMode', draftOfflineMode);
          setIsEvaluatingOfflineStrategy(false);
        }}
        onCancelOfflineStrategyEvaluation={() => {
          setDraftOfflineMode(config.offlineMode || 'follow_apps');
          setIsEvaluatingOfflineStrategy(false);
        }}
      />
    );
    if (activeZone === 'chatbot') return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {effectiveLaunchStyle === 'chat_first' && (
          <DockStylePanel
            config={config}
            onChange={onChange}
            isGrowthPlus={isGrowthPlus}
            isAgentPlus={isAgentPlus}
            deviceMode={deviceMode}
            onLaunchStyleChange={(value) => {
              setSelectedChannelId(null);
              setShowTapHint(false);
              setPreviewSupportOpen(false);
              if (value === 'chat_first') {
                setDockState('chat');
                setActiveZone('chatbot');
                return;
              }
              setDockState(value === 'container' ? 'allApps' : 'expanded');
              setActiveZone('dock');
            }}
          />
        )}
        <ChatHeaderPanel config={config} onChange={onChange} isGrowthPlus={isGrowthPlus} />
        <ChatBodyPanel config={config} onChange={onChange} isGrowthPlus={isGrowthPlus} />
        {effectiveLaunchStyle === 'chat_first' && (
          <ChannelsPanel config={config} onChange={onChange} onSelectChannel={id => { setSelectedChannelId(id); setActiveZone('app_item'); }} />
        )}
      </div>
    );
    if (activeZone === 'dock') return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <DockStylePanel
          config={config}
          onChange={onChange}
          isGrowthPlus={isGrowthPlus}
          isAgentPlus={isAgentPlus}
          deviceMode={deviceMode}
          onLaunchStyleChange={(value) => {
            setSelectedChannelId(null);
            setShowTapHint(false);
            setPreviewSupportOpen(false);
            if (value === 'chat_first') {
              setDockState('chat');
              setActiveZone('chatbot');
              return;
            }
            setDockState(value === 'container' ? 'allApps' : 'expanded');
            setActiveZone('dock');
          }}
        />
        <ChannelsPanel config={config} onChange={onChange} onSelectChannel={id => { setSelectedChannelId(id); setActiveZone('app_item'); }} />
      </div>
    );
    if (activeZone === 'app_item' && selectedChannelId) return <ChannelItemPanel config={config} onChange={onChange} channelId={selectedChannelId} isGrowthPlus={isGrowthPlus} onBack={() => {
      setSelectedChannelId(null);
      if (effectiveLaunchStyle === 'chat_first') {
        setDockState('chat');
        setActiveZone('chatbot');
        return;
      }
      setDockState(effectiveLaunchStyle === 'container' ? 'allApps' : 'expanded');
      setActiveZone('dock');
    }} />;

    return null;
  };

  return (
    <div style={{ display: 'flex', background: 'var(--dock-editor-bg)', height: '100%', width: '100%', overflow: 'hidden' }}>
      <style>{DOCK_CSS}</style>

      {/* ── Canvas (left 66%) ── */}
      <div style={{ flex: '0 0 66.6%', display: 'flex', flexDirection: 'column' }}>

        {/* Toolbar */}
        <div style={{ minHeight: 72, background: 'var(--dock-toolbar-bg)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', gap: 12, flexShrink: 0, zIndex: 10, borderBottom: '1px solid var(--dock-toolbar-border)', boxShadow: 'var(--dock-toolbar-shadow)' }}>
          <div style={{ display: 'flex', alignItems: 'center', background: 'var(--dock-toolbar-group-bg)', borderRadius: 12, padding: '6px 6px 6px 12px', border: '1px solid var(--dock-toolbar-group-border)', width: 720, maxWidth: '100%', transition: 'all 0.2s', boxShadow: 'var(--dock-toolbar-group-shadow)' }}>
            <Globe size={15} color="var(--dock-toolbar-input-icon)" style={{ flexShrink: 0 }} />
            <input
              value={draftUrl}
              onChange={e => setDraftUrl(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && loadProxy()}
              placeholder="Enter store URL (e.g. yourstore.com)"
              style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--dock-toolbar-input-text)', padding: '10px' }}
            />
            <button
              onClick={() => loadProxy()}
              disabled={isLoadingProxy}
              style={{
                background: 'var(--dock-toolbar-preview-btn-bg)',
                color: 'var(--dock-toolbar-preview-btn-text)',
                border: 'none',
                marginRight: 12,
                borderRadius: 6,
                padding: '6px 16px',
                fontSize: 12,
                fontWeight: 600,
                cursor: isLoadingProxy ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: isLoadingProxy ? 0.7 : 1,
                whiteSpace: 'nowrap'
              }}
            >
              {isLoadingProxy ? 'Loading...' : 'Preview Shop'}
            </button>
            <Tooltip content="Must be publicly accessible — password-protected or coming-soon pages won't load. Temporarily disable your storefront password if needed.">
              <button style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', marginRight: 8 }}>
                <Lock size={16} style={{ color: 'var(--ink-subtle)' }} />
              </button>
            </Tooltip>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--dock-toolbar-group-bg)', border: '1px solid var(--dock-toolbar-group-border)', borderRadius: 12, padding: '6px 8px', boxShadow: 'var(--dock-toolbar-group-shadow)' }}>
            {/* Direct access to reposition tool */}
            {/* <button
              onClick={startDragMode}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: 'rgba(216, 27, 96, 0.15)',
                color: '#fff',
                border: '1px solid rgba(216, 27, 96, 0.3)',
                padding: '6px 12px',
                borderRadius: 8,
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s',
                marginRight: 8
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(216, 27, 96, 0.25)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(216, 27, 96, 0.15)'}
            >
              <Move size={14} />
              Adjust Button Position
            </button> */}

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: 'var(--dock-toolbar-device-label)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Device</span>
              <div style={{
                position: 'relative',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                alignItems: 'stretch',
                padding: 1,
                minWidth: 192,
                borderRadius: 10,
                border: '1px solid color-mix(in srgb, var(--dock-toolbar-device-border) 82%, white 18%)',
                background: 'linear-gradient(180deg, color-mix(in srgb, var(--dock-toolbar-device-bg) 82%, white 18%) 0%, var(--dock-toolbar-device-bg) 100%)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45), 0 8px 18px rgba(15,23,42,0.08)',
                overflow: 'hidden',
              }}>
                <motion.div
                  initial={false}
                  animate={{ x: deviceMode === 'desktop' ? '0%' : '100%' }}
                  transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                  style={{
                    position: 'absolute',
                    top: 1,
                    left: 1,
                    width: 'calc(50% - 1px)',
                    height: 'calc(100% - 2px)',
                    borderRadius: 7,
                    background: 'linear-gradient(135deg, var(--dock-toolbar-preview-btn-bg) 0%, color-mix(in srgb, var(--dock-toolbar-preview-btn-bg) 84%, #93c5fd 16%) 100%)',
                    boxShadow: '0 6px 16px rgba(37,99,235,0.22), inset 0 1px 0 rgba(255,255,255,0.22)',
                    border: '1px solid rgba(59,130,246,0.55)',
                  }}
                />
                <button
                  onClick={() => { setDeviceMode('desktop'); setDockState('closed'); setActiveZone(null); }}
                  title="Desktop preview"
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    height: 32,
                    padding: '0 10px',
                    borderRadius: 7,
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    transition: 'color 0.2s ease, transform 0.2s ease',
                    background: 'transparent',
                    color: deviceMode === 'desktop' ? '#ffffff' : 'var(--dock-toolbar-device-inactive-text)',
                    fontSize: 11,
                    fontWeight: deviceMode === 'desktop' ? 700 : 600,
                  }}
                >
                  <Monitor size={15} />
                  <span>Desktop</span>
                </button>
                <button
                  onClick={() => { setDeviceMode('mobile'); setDockState('closed'); setActiveZone(null); }}
                  title="Mobile preview"
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    height: 32,
                    padding: '0 10px',
                    borderRadius: 7,
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    transition: 'color 0.2s ease, transform 0.2s ease',
                    background: 'transparent',
                    color: deviceMode === 'mobile' ? '#ffffff' : 'var(--dock-toolbar-device-inactive-text)',
                    fontSize: 11,
                    fontWeight: deviceMode === 'mobile' ? 700 : 600,
                  }}
                >
                  <Smartphone size={15} />
                  <span>Mobile</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Canvas */}
        <div
          ref={canvasRef}
          style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: isMobile ? 'center' : 'stretch', justifyContent: isMobile ? 'center' : 'stretch', background: isMobile ? 'var(--dock-canvas-toolbar-start)' : 'transparent', cursor: isDragging ? 'grabbing' : 'default' }}
          onClick={() => {
            if (isDragMode) return;
            activeZone && setActiveZone(null);
          }}
          onPointerMove={handleCanvasPointerMove}
          onPointerUp={handleCanvasPointerUp}
        >
          <div
            style={{
              position: 'absolute',
              top: -120,
              left: '12%',
              width: '76%',
              height: 240,
              borderRadius: 999,
              background: 'radial-gradient(ellipse at center, color-mix(in srgb, var(--theme-action-accent) 20%, transparent) 0%, transparent 72%)',
              pointerEvents: 'none',
              zIndex: 0
            }}
          />
          {mountedPreviewModes.mobile && (
            /* ── #7: iPhone frame ── */
            <div style={{ position: 'relative', width: 390, height: 'min(780px, calc(100% - 32px))', flexShrink: 0, borderRadius: 48, border: '10px solid var(--dock-canvas-toolbar-start)', boxShadow: '0 0 0 2px var(--reach-shell-border), 0 40px 100px rgba(216, 27, 96, 0.12)', overflow: 'hidden', background: '#000', display: isMobile ? 'block' : 'none' }}>
              {/* Screen content */}
              <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
                {proxyHtml && (
                  <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                    <iframe
                      ref={mobileIframeRef}
                      srcDoc={proxyHtml}
                      title="Store preview"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        pointerEvents: 'none',
                        zIndex: 1,
                        background: 'white',
                        opacity: 1,
                        filter: 'none',
                        transition: 'all 0.3s'
                      }}
                      sandbox="allow-same-origin"
                    />
                  </div>
                )}
                {!proxyHtml && <MockWebpage />}

                {/* Drag Indicator Overlay — DASHED CORNER BOXES */}
                {(isDragMode || isDragging) && (
                  <>
                    <div style={{ position: 'absolute', left: 0, bottom: 0, width: 140, height: 140, border: `4px dashed ${dragZone === 'left' ? 'rgba(56, 189, 248, 0.9)' : 'rgba(255,255,255,0.4)'}`, background: dragZone === 'left' ? 'rgba(56, 189, 248, 0.15)' : 'rgba(255,255,255,0.05)', zIndex: 5, pointerEvents: 'none', borderRadius: 24, transition: 'all 0.2s' }} />
                    <div style={{ position: 'absolute', right: 0, bottom: 0, width: 140, height: 140, border: `4px dashed ${dragZone === 'right' ? 'rgba(56, 189, 248, 0.9)' : 'rgba(255,255,255,0.4)'}`, background: dragZone === 'right' ? 'rgba(56, 189, 248, 0.15)' : 'rgba(255,255,255,0.05)', zIndex: 5, pointerEvents: 'none', borderRadius: 24, transition: 'all 0.2s' }} />
                  </>
                )}

                {proxyHtml && <div ref={mobileOverlayRef} style={{ position: 'absolute', inset: 0, zIndex: 40 }} />}

                {config.isEnabled && (
                  <DockPreview
                    config={previewConfig} dockState={dockState} setDockState={setDockState}
                    activeZone={activeZone} onZoneClick={handleZoneClick}
                    isMobile={true} selectedChannelId={selectedChannelId} setSelectedChannelId={setSelectedChannelId}
                    isDragging={isDragging || isDragMode} dragPos={dragPos} onPointerDown={handleButtonPointerDown}
                    canvasW={canvasWidth}
                    canvasH={canvasHeight}
                    isDragMode={isDragMode}
                    onCommit={commitDragMode}
                    onCancel={cancelDragMode}
                    showTapHint={showTapHint}
                    setShowTapHint={setShowTapHint}
                    effectivePosition={pendingConfig?.widgetPosition ?? config.widgetPosition}
                    isEvaluatingOfflineStrategy={isEvaluatingOfflineStrategy}
                  />
                )}

                {/* Adaptive Chat Window Mockup for Mobile */}
                {renderChatMockup(true)}

                {/* Mobile drawer — rendered at screen level so it fills the full phone width */}
                {config.isEnabled && dockState === 'allApps' && (() => {
                  const apps = config.apps || {};
                  const drawerApps = Object.entries(apps)
                    .filter(([id, s]: [string, any]) => id !== 'chatbot' && s.enabled !== false && CHANNEL_META[id.toLowerCase()])
                    .sort((a, b) => (a[1].order ?? 0) - (b[1].order ?? 0));
                  const showMobileContainerTitle = effectiveLaunchStyle === 'container' && config.mobileContainerTitleEnabled === true;
                  const mobileContainerTitle = (config.mobileContainerTitle || '').trim() || 'Get in Touch';
                  const mobileContainerTitleAlign = config.mobileContainerTitleAlign || 'center';
                  const showMobileContainerLabels = effectiveLaunchStyle === 'container'
                    ? config.mobileContainerLabels !== false
                    : true;
                  const canShowMobileChatbot = (config.planTier === 'rep' || config.planTier === 'vea' || config.planTier === 'agent')
                    && config.apps?.chatbot?.enabled !== false;
                  return (
                    <AnimatePresence>
                      <motion.div
                        key="mobile-drawer-overlay"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        style={{
                          position: 'absolute', inset: 0, zIndex: 500,
                          background: 'rgba(0,0,0,0.45)',
                          display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                        }}
                        onClick={() => { setShowTapHint(false); setDockState('closed'); setActiveZone(null); }}
                      >
                        <motion.div
                          initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
                          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                          className="dock-zone"
                          style={{
                            background: mobileSheetGlass.sheetBg,
                            backdropFilter: mobileSheetGlass.backdropFilter === 'none' ? 'none' : 'blur(28px) saturate(180%)',
                            WebkitBackdropFilter: mobileSheetGlass.backdropFilter === 'none' ? 'none' : 'blur(28px) saturate(180%)',
                            borderTop: `1px solid ${mobileSheetGlass.sheetBorder}`,
                            borderTopLeftRadius: 24, borderTopRightRadius: 24,
                            borderBottomLeftRadius: 38, borderBottomRightRadius: 38,
                            padding: config.planTier === 'free' ? '12px 20px 40px' : '12px 20px 20px',
                            display: 'flex', flexDirection: 'column', gap: 16,
                            height: '62%', width: '100%', color: mobileSheetGlass.textColor,
                            flexShrink: 0,
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                            minHeight: 0,
                            outline: activeZone === 'dock' ? '2px solid var(--theme-action-accent)' : undefined,
                            outlineOffset: activeZone === 'dock' ? -2 : undefined,
                          }}
                          onClick={e => { e.stopPropagation(); handleZoneClick('dock'); }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                            <button
                              onClick={e => { e.stopPropagation(); setShowTapHint(false); setDockState('closed'); setActiveZone(null); }}
                              aria-label="Close drawer"
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: '50%',
                                background: mobileSheetGlass.xBg,
                                border: mobileSheetGlass.xBorder.includes('solid') ? mobileSheetGlass.xBorder : `1px solid ${mobileSheetGlass.xBorder}`,
                                boxShadow: mobileSheetGlass.xShadow,
                                color: mobileSheetGlass.xColor,
                                backdropFilter: mobileSheetGlass.xBackdropFilter,
                                WebkitBackdropFilter: mobileSheetGlass.xBackdropFilter,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                padding: 0,
                                flexShrink: 0,
                              }}
                            >
                              <X size={16} />
                            </button>
                          </div>

                          {showMobileContainerTitle && (
                            <div style={{ display: 'flex', justifyContent: mobileContainerTitleAlign === 'left' ? 'flex-start' : mobileContainerTitleAlign === 'right' ? 'flex-end' : 'center', marginTop: -2, marginBottom: 8 }}>
                              <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.01em', color: mobileSheetGlass.textColor, opacity: 0.72, textAlign: mobileContainerTitleAlign }}>
                                {mobileContainerTitle}
                              </div>
                            </div>
                          )}

                          {/* Grid 4 columns */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px 8px', overflowY: 'auto', flex: 1, minHeight: 0, alignContent: 'start' }}>
                            {drawerApps.map(([rawId, app]: [string, any]) => {
                              const id = rawId.toLowerCase();
                              const meta = CHANNEL_META[id];
                              const Icon = meta?.icon;
                              const bgColor = app.color || meta?.color || '#333';
                              const iconColor = getBrandIconColor(bgColor);

                              return (
                                <div key={id}
                                  className={`dock-zone${activeZone === 'app_item' && selectedChannelId === id ? ' dock-active' : ''}`}
                                  ref={el => {
                                    const refKey = effectiveLaunchStyle === 'container' ? `mobile-container-${id}` : null;
                                    if (!refKey) return;
                                    if (el) mobileDrawerIconRefs.current.set(refKey, el);
                                    else mobileDrawerIconRefs.current.delete(refKey);
                                  }}
                                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}
                                  onMouseEnter={() => { if (effectiveLaunchStyle === 'container') setMobileDrawerHoveredApp(`mobile-container-${id}`); }}
                                  onMouseLeave={() => { if (effectiveLaunchStyle === 'container') setMobileDrawerHoveredApp(null); }}
                                  onClick={e => { e.stopPropagation(); setSelectedChannelId(id); handleZoneClick('app_item'); }}>
                                  <div style={{ width: 54, height: 54, borderRadius: typeof config.mobileRadius === 'number' ? config.mobileRadius : ((config.mobileButtonSize ?? 52) / 2), background: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.2)', border: bgColor.toLowerCase() === '#ffffff' ? '1px solid #e2e8f0' : 'none' }}>
                                    {Icon && <Icon size={24} color={iconColor} />}
                                  </div>
                                  {showMobileContainerLabels && (
                                    <span style={{ fontSize: 10, fontWeight: 600, color: mobileSheetGlass.textColor, opacity: 0.85, textAlign: 'center', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{app.label || id}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Chatbot entry — container mode only */}
                          {effectiveLaunchStyle === 'container' && canShowMobileChatbot && (() => {
                            const drawerAnchorBg = config.anchorColor || '#000000';
                            const drawerAnchorText = getContrastColor(drawerAnchorBg);
                            return (
                              <div
                                className={`dock-zone${activeZone === 'chatbot' ? ' dock-active' : ''}`}
                                onClick={e => { e.stopPropagation(); setDockState('chat'); handleZoneClick('chatbot'); }}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 8,
                                  background: mobileContainerGlass.chatZoneBg,
                                  border: mobileContainerGlass.chatZoneBorder || `1px solid ${mobileContainerGlass.border}`,
                                  borderRadius: 9999, padding: '4px 4px 4px 10px',
                                  cursor: 'pointer', flexShrink: 0, minHeight: 50,
                                }}
                              >
                                <div style={{ width: 38, height: 38, borderRadius: '50%', background: drawerAnchorBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: `0 2px 8px ${drawerAnchorBg}60` }}>
                                  {config.chatbotEntryAvatar
                                    ? <img src={config.chatbotEntryAvatar} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'contain', padding: 4, boxSizing: 'border-box', display: 'block' }} />
                                    : <Bot size={18} color={drawerAnchorText} />}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 5, flex: 1, minWidth: 0 }}>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 600, color: mobileContainerGlass.textColor, flexShrink: 1, minWidth: 0 }}>
                                    {config.apps?.chatbot?.label || 'Ask Assistant...'}
                                  </span>
                                  <div style={{ display: 'flex', gap: 3, alignItems: 'center', flexShrink: 0 }}>
                                    <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut', delay: 0 }} style={{ width: 5, height: 5, borderRadius: '50%', background: mobileContainerGlass.textColor }} />
                                    <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut', delay: 0.2 }} style={{ width: 5, height: 5, borderRadius: '50%', background: mobileContainerGlass.textColor }} />
                                    <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }} style={{ width: 5, height: 5, borderRadius: '50%', background: mobileContainerGlass.textColor }} />
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Footer branding — free tier only */}
                          {config.planTier === 'free' && (
                            <div style={{ display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                              <span style={{ fontSize: 9, fontWeight: 700, color: mobileSheetGlass.textColor, opacity: 0.3, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Powered by VeaHub</span>
                            </div>
                          )}
                        </motion.div>
                      </motion.div>
                    </AnimatePresence>
                  );
                })()}

                {mobileDrawerHoveredApp && effectiveLaunchStyle === 'container' && config.mobileContainerTooltips !== false && (() => {
                  const el = mobileDrawerIconRefs.current.get(mobileDrawerHoveredApp);
                  if (!el) return null;
                  const rawId = mobileDrawerHoveredApp.slice('mobile-container-'.length);
                  const rect = el.getBoundingClientRect();
                  const label = (config.apps as any)?.[rawId]?.label || CHANNEL_META[rawId]?.label || rawId;
                  return (
                    <div style={{
                      position: 'fixed',
                      left: rect.left + rect.width / 2,
                      top: rect.top - 10,
                      transform: 'translateX(-50%) translateY(-100%)',
                      zIndex: 9999,
                      background: 'rgba(12,12,14,0.9)',
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      color: '#ffffff',
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: '0.01em',
                      padding: '4px 10px 5px',
                      borderRadius: 8,
                      whiteSpace: 'nowrap',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                      pointerEvents: 'none',
                    }}>
                      {label}
                    </div>
                  );
                })()}

                {!config.isEnabled && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(4px)' }}>
                    <Text as="p" variant="bodyMd" tone="subdued">Widget disabled</Text>
                  </div>
                )}
              </div>
              {/* Home indicator */}
              <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', width: 120, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.4)', zIndex: 20 }} />
            </div>
          )}
          {mountedPreviewModes.desktop && (
            /* ── Desktop canvas ── */
            <div style={{ position: 'absolute', inset: 0, display: isMobile ? 'none' : 'block' }}>
              {proxyHtml && (() => {
                const desktopScale = canvasWidth / DESKTOP_PREVIEW_WIDTH;
                const iframeHeightDesktop = canvasHeight / desktopScale;
                return (
                  <div style={{ position: 'relative', width: '100%', height: '100%', zIndex: 1 }}>
                    <iframe
                      ref={desktopIframeRef}
                      srcDoc={proxyHtml}
                      title="Store preview"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: `${DESKTOP_PREVIEW_WIDTH}px`,
                        height: `${iframeHeightDesktop}px`,
                        border: 'none',
                        pointerEvents: 'none',
                        transform: `scale(${desktopScale})`,
                        transformOrigin: 'top left',
                        background: 'white',
                        opacity: 1,
                        filter: 'none',
                        transition: 'all 0.3s'
                      }}
                      sandbox="allow-same-origin"
                    />
                  </div>
                );
              })()}
              {!proxyHtml && <MockWebpage />}

              {/* Drag Indicator Overlay — DASHED CORNER BOXES */}
              {(isDragMode || isDragging) && (
                <>
                  <div style={{ position: 'absolute', left: 0, bottom: 0, width: 140, height: 140, border: `4px dashed ${dragZone === 'left' ? 'rgba(56, 189, 248, 0.9)' : 'rgba(255,255,255,0.4)'}`, background: dragZone === 'left' ? 'rgba(56, 189, 248, 0.15)' : 'rgba(255,255,255,0.1)', zIndex: 5, pointerEvents: 'none', borderRadius: 24, transition: 'all 0.2s' }} />
                  <div style={{ position: 'absolute', right: 0, bottom: 0, width: 140, height: 140, border: `4px dashed ${dragZone === 'right' ? 'rgba(56, 189, 248, 0.9)' : 'rgba(255,255,255,0.4)'}`, background: dragZone === 'right' ? 'rgba(56, 189, 248, 0.15)' : 'rgba(255,255,255,0.1)', zIndex: 5, pointerEvents: 'none', borderRadius: 24, transition: 'all 0.2s' }} />
                </>
              )}

              {proxyHtml && <div ref={desktopOverlayRef} style={{ position: 'absolute', inset: 0, zIndex: 40 }} />}

              {config.isEnabled && (
                <DockPreview
                    config={previewConfig} dockState={dockState} setDockState={setDockState}
                  activeZone={activeZone} onZoneClick={handleZoneClick}
                  isMobile={false} selectedChannelId={selectedChannelId} setSelectedChannelId={setSelectedChannelId}
                  isDragging={isDragging || isDragMode} dragPos={dragPos} onPointerDown={handleButtonPointerDown}
                  canvasW={canvasWidth}
                  canvasH={canvasHeight}
                  isDragMode={isDragMode}
                  onCommit={commitDragMode}
                  onCancel={cancelDragMode}
                    showTapHint={showTapHint}
                    setShowTapHint={setShowTapHint}
                    effectivePosition={pendingConfig?.widgetPosition ?? config.widgetPosition}
                    isEvaluatingOfflineStrategy={isEvaluatingOfflineStrategy}
                  />
              )}

              {/* Chat Window Mockup for Desktop */}
              {renderChatMockup(false)}
              {!config.isEnabled && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(4px)' }}>
                  <Text as="p" variant="bodyMd" tone="subdued">Widget is disabled — enable it in Anchor Button settings</Text>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Settings Panel (right 33.4%) ── */}
      <div style={{ flex: '0 0 33.4%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderLeft: '1px solid var(--dock-column-divider)' }}>

        {/* Panel header */}
        <div style={{ background: 'var(--dock-panel-header-bg)', minHeight: 72, display: 'flex', alignItems: 'center', padding: '12px 16px', gap: 10, flexShrink: 0, borderBottom: '1px solid var(--dock-panel-header-border)', boxShadow: 'var(--dock-panel-header-shadow)' }}>
          {activeZone && (
            <button onClick={handleBack} style={{ width: 32, height: 32, background: 'rgba(255,255,255,0.72)', border: '1px solid rgba(219,229,239,0.95)', cursor: 'pointer', padding: 0, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--dock-panel-header-text)', boxShadow: '0 2px 8px rgba(15,23,42,0.05)' }}>
              <ChevronLeft size={16} />
            </button>
          )}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, minWidth: 0 }}>
              <Text as="h2" variant="headingMd" fontWeight="semibold">
                {activeZone ? ZONE_TITLES[activeZone] : 'Editor'}
              </Text>
              <AnimatePresence mode="wait" initial={false}>
                {(showExpandHint || showZoneHint) && (() => {
                  const tipText = showExpandHint
                    ? (isDragMode
                        ? 'Drag the button to your preferred corner'
                        : effectiveLaunchStyle === 'chat_first'
                          ? (isMobile ? 'Tap the launcher again to open chat' : 'Click the launcher again to open chat')
                          : isMobile
                            ? `Tap the button again to expand the ${effectiveLaunchStyle === 'container' ? 'container' : 'dock'}`
                            : `Click the button again to expand the ${effectiveLaunchStyle === 'container' ? 'container' : 'dock'}`)
                    : 'Hover over a zone, then click to edit its settings';
                  return (
                    <motion.div
                      key={tipText}
                      initial={{ opacity: 0, scale: 0.985, y: 6 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        scale: [1, 1.035, 1],
                        boxShadow: [
                          '0 3px 10px color-mix(in srgb, var(--theme-action-accent) 10%, transparent)',
                          '0 6px 16px color-mix(in srgb, var(--theme-action-accent) 18%, transparent)',
                          '0 3px 10px color-mix(in srgb, var(--theme-action-accent) 10%, transparent)',
                        ],
                      }}
                      exit={{ opacity: 0, scale: 0.99, y: -6 }}
                      transition={{ duration: 0.34, ease: 'easeOut' }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        maxWidth: '70%',
                        flexShrink: 1,
                        background: 'color-mix(in srgb, var(--theme-action-accent) 12%, white)',
                        color: 'color-mix(in srgb, var(--theme-action-accent) 78%, #1f2937)',
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: '0.01em',
                        padding: '7px 11px',
                        borderRadius: 11,
                        border: '1px solid color-mix(in srgb, var(--theme-action-accent) 22%, #dbe5ef)',
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {tipText}
                    </motion.div>
                  );
                })()}
              </AnimatePresence>
            </div>
          </div>

          {activeZone && (
            <button onClick={() => { setActiveZone(null); setSelectedChannelId(null); }} style={{ width: 32, height: 32, background: 'rgba(255,255,255,0.72)', border: '1px solid rgba(219,229,239,0.95)', cursor: 'pointer', padding: 0, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--dock-panel-header-text)', boxShadow: '0 2px 8px rgba(15,23,42,0.05)' }}>
              <X size={16} />
            </button>
          )}
        </div>

        {/* Panel body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16, position: 'relative', background: 'var(--dock-panel-body-bg)' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeZone || 'global'}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {renderPanel()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--dock-panel-footer-border)', flexShrink: 0 }}>
          {isDirty
            ? <button
              className="dock-save-btn"
              onClick={onSave}
              style={{
                width: '100%',
                background: 'var(--dock-panel-save-gradient)',
                position: 'relative',
                overflow: 'hidden',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '10px 16px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Save Changes
            </button>
            : <Text as="p" variant="bodySm" tone="subdued" alignment="center">All changes saved</Text>}
        </div>
      </div>

      {showSaveToast && (
        <Toast content="Position committed! Don't forget to hit Save to apply changes to the actual button." onDismiss={toggleSaveToast} duration={5000} />
      )}
    </div>
  );
}
